import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Tag, CreditCard, Users, Pencil, Shield, ShieldCheck, Truck, Package, ShoppingBag, Link2, Save, X, Cloud, CloudOff, RefreshCw, Settings, Key, Eye, EyeOff, AlertTriangle, CheckCircle2, Bell, Mail, Wrench, Boxes } from "lucide-react";

const COLORS = [
  "#F43F5E", "#10B981", "#F59E0B", "#3B82F6", "#8B5CF6", 
  "#EC4899", "#14B8A6", "#84CC16", "#EF4444", "#0EA5E9",
  "#22C55E", "#D946EF", "#6366F1", "#F97316", "#06B6D4"
];

const SettingsPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [internalProducts, setInternalProducts] = useState([]); // Productos internos (margin_categories)
  const [loading, setLoading] = useState(true);
  
  // Shopify mapping state
  const [pendingMappings, setPendingMappings] = useState({}); // { productId: categoryId }
  const [savingMappings, setSavingMappings] = useState(false);
  
  // Odoo sync state
  const [odooStatus, setOdooStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [odooCredentials, setOdooCredentials] = useState({ url: "", db: "", username: "", api_key: "" });
  const [showOdooConfig, setShowOdooConfig] = useState(false);
  const [savingOdooCredentials, setSavingOdooCredentials] = useState(false);
  
  // Duplicate cleanup state
  const [duplicateStats, setDuplicateStats] = useState(null);

  // Inventory locations visibility state
  const [invAllLocations, setInvAllLocations] = useState({ odoo: [], shopify: [] });
  const [invHidden, setInvHidden] = useState([]);  // list of {platform, location_id, name}
  const [invLocLoading, setInvLocLoading] = useState(false);
  const [invLocSaving, setInvLocSaving] = useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);
  
  // Reimport state
  const [reimporting, setReimporting] = useState(false);
  
  // Update SKUs state
  const [updatingSkus, setUpdatingSkus] = useState(false);
  
  // Import products state
  const [importingOdooProducts, setImportingOdooProducts] = useState(false);
  const [importingShopifyProducts, setImportingShopifyProducts] = useState(false);
  
  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({ notification_email: "", batch_recovery_notifications: true });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Shopify sync state  
  const [shopifyStatus, setShopifyStatus] = useState(null);
  const [syncingShopify, setSyncingShopify] = useState(false);
  
  // Shopify credentials state
  const [showShopifyConfig, setShowShopifyConfig] = useState(false);
  const [shopifyCredentials, setShopifyCredentials] = useState({ store_url: "", client_id: "", client_secret: "" });
  const [savingShopifyCredentials, setSavingShopifyCredentials] = useState(false);
  
  // Category form
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: "", type: "expense", color: "#F43F5E" });
  
  // Payment method form
  const [methodDialogOpen, setMethodDialogOpen] = useState(false);
  const [newMethod, setNewMethod] = useState({ name: "" });
  
  // User form
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", role: "manager", assigned_categories: [] });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchData();
    if (isAdmin) {
      fetchOdooStatus();
      fetchShopifyStatus();
      fetchNotificationSettings();
      fetchInventoryLocations();
    }
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/notifications`, getAuthHeaders());
      setNotificationSettings({
        notification_email: res.data.notification_email || "",
        batch_recovery_notifications: res.data.batch_recovery_notifications ?? true
      });
    } catch (error) {
      console.error("Error fetching notification settings:", error);
    }
  };

  const saveNotificationSettings = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(`${API}/settings/notifications`, {
        notification_email: notificationSettings.notification_email || null,
        batch_recovery_notifications: notificationSettings.batch_recovery_notifications
      }, getAuthHeaders());
      toast.success("Configuración de notificaciones guardada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar configuración");
    } finally {
      setSavingNotifications(false);
    }
  };

  const sendTestEmail = async () => {
    if (!notificationSettings.notification_email) {
      toast.error("Primero configura un correo de notificación");
      return;
    }
    setSendingTestEmail(true);
    try {
      const res = await axios.post(`${API}/settings/notifications/test`, {}, getAuthHeaders());
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar correo de prueba");
    } finally {
      setSendingTestEmail(false);
    }
  };

  // ----- Inventory Locations visibility -----
  const fetchInventoryLocations = async () => {
    setInvLocLoading(true);
    try {
      const [allR, hidR] = await Promise.all([
        axios.get(`${API}/inventory-write/all-locations`, getAuthHeaders()),
        axios.get(`${API}/inventory-write/hidden-locations`, getAuthHeaders()),
      ]);
      setInvAllLocations({ odoo: allR.data?.odoo || [], shopify: allR.data?.shopify || [] });
      setInvHidden(hidR.data?.hidden || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar ubicaciones");
    } finally {
      setInvLocLoading(false);
    }
  };

  const isLocationHidden = (platform, id) =>
    invHidden.some((h) => h.platform === platform && String(h.location_id) === String(id));

  const toggleLocationHidden = async (platform, loc) => {
    const already = isLocationHidden(platform, loc.id);
    const next = already
      ? invHidden.filter((h) => !(h.platform === platform && String(h.location_id) === String(loc.id)))
      : [...invHidden, { platform, location_id: String(loc.id), name: loc.name }];
    setInvLocSaving(true);
    try {
      await axios.put(`${API}/inventory-write/hidden-locations`, { hidden: next }, getAuthHeaders());
      setInvHidden(next);
      toast.success(already ? "Ubicación visible de nuevo" : "Ubicación oculta");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setInvLocSaving(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const requests = [
        axios.get(`${API}/categories`, getAuthHeaders()),
        axios.get(`${API}/payment-methods`, getAuthHeaders()),
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/margin-categories`, getAuthHeaders()) // Productos internos para proveedores
      ];
      
      // Only fetch users if admin
      if (isAdmin) {
        requests.push(axios.get(`${API}/users`, getAuthHeaders()));
      }
      
      const responses = await Promise.all(requests);
      setCategories(responses[0].data);
      setPaymentMethods(responses[1].data);
      setProducts(responses[2].data);
      setInternalProducts(responses[3].data || []);
      
      if (isAdmin && responses[4]) {
        setUsers(responses[4].data);
      }
    } catch (error) {
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  // Category handlers
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/categories`, newCategory, getAuthHeaders());
      toast.success("Categoría creada");
      setCategoryDialogOpen(false);
      setNewCategory({ name: "", type: "expense", color: "#F43F5E" });
      fetchData();
    } catch (error) {
      toast.error("Error al crear la categoría");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!confirm("¿Estás seguro de eliminar esta categoría?")) return;
    
    try {
      await axios.delete(`${API}/categories/${id}`, getAuthHeaders());
      toast.success("Categoría eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar la categoría");
    }
  };

  // Payment method handlers
  const handleCreateMethod = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payment-methods`, newMethod, getAuthHeaders());
      toast.success("Método de pago creado");
      setMethodDialogOpen(false);
      setNewMethod({ name: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al crear el método de pago");
    }
  };

  const handleDeleteMethod = async (id) => {
    if (!confirm("¿Estás seguro de eliminar este método de pago?")) return;
    
    try {
      await axios.delete(`${API}/payment-methods/${id}`, getAuthHeaders());
      toast.success("Método de pago eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar el método de pago");
    }
  };

  // User handlers
  const resetUserForm = () => {
    setUserForm({ name: "", email: "", password: "", role: "manager", assigned_categories: [] });
    setEditingUser(null);
  };

  const handleOpenUserDialog = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setUserForm({
        name: userToEdit.name,
        email: userToEdit.email,
        password: "",
        role: userToEdit.role,
        assigned_categories: userToEdit.assigned_categories || []
      });
    } else {
      resetUserForm();
    }
    setUserDialogOpen(true);
  };

  const handleToggleProductAssignment = (productId) => {
    setUserForm(prev => {
      const current = prev.assigned_categories || [];
      if (current.includes(productId)) {
        return { ...prev, assigned_categories: current.filter(id => id !== productId) };
      } else {
        return { ...prev, assigned_categories: [...current, productId] };
      }
    });
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user
        const updateData = {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role
        };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        // Include assigned_categories for suppliers
        if (userForm.role === 'proveedor') {
          updateData.assigned_categories = userForm.assigned_categories;
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData, getAuthHeaders());
        toast.success("Usuario actualizado");
      } else {
        // Create user
        const createData = { ...userForm };
        if (createData.role !== 'proveedor') {
          delete createData.assigned_categories;
        }
        await axios.post(`${API}/users`, createData, getAuthHeaders());
        toast.success("Usuario creado");
      }
      setUserDialogOpen(false);
      resetUserForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el usuario");
    }
  };

  const handleToggleUserActive = async (userId, isActive) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_active: !isActive }, getAuthHeaders());
      toast.success(isActive ? "Usuario desactivado" : "Usuario activado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado del usuario");
    }
  };

  const handleToggleCanEditInventory = async (userId, currentValue) => {
    try {
      await axios.put(
        `${API}/users/${userId}`,
        { can_edit_inventory: !currentValue },
        getAuthHeaders()
      );
      toast.success(
        !currentValue
          ? "Permiso de editar inventario otorgado"
          : "Permiso de editar inventario revocado"
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar el permiso");
    }
  };

  const handleDeleteUser = async (id) => {
    if (!confirm("¿Estás seguro de eliminar este usuario?")) return;
    
    try {
      await axios.delete(`${API}/users/${id}`, getAuthHeaders());
      toast.success("Usuario eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar el usuario");
    }
  };

  // Shopify mapping handlers
  const handleLocalMappingChange = (productId, parentCategoryId) => {
    setPendingMappings(prev => ({
      ...prev,
      [productId]: parentCategoryId === "none" ? null : parentCategoryId
    }));
  };

  // Odoo sync functions
  const fetchOdooStatus = async () => {
    try {
      const response = await axios.get(`${API}/odoo/status`, getAuthHeaders());
      setOdooStatus(response.data);
    } catch (error) {
      console.log("Odoo status not available");
    }
  };

  const handleOdooSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/odoo/sync`, null, getAuthHeaders());
      if (response.data.success) {
        toast.success(`Sincronización completada: ${response.data.synced} ventas importadas`);
        fetchOdooStatus();
      } else {
        toast.error(response.data.error || "Error en sincronización");
      }
    } catch (error) {
      toast.error("Error al sincronizar con Odoo");
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleAutoSync = async () => {
    try {
      const newStatus = !odooStatus?.sync_enabled;
      const response = await axios.post(`${API}/odoo/toggle-auto-sync?enable=${newStatus}`, null, getAuthHeaders());
      if (response.data.success) {
        toast.success(response.data.message);
        fetchOdooStatus();
      }
    } catch (error) {
      toast.error("Error al cambiar configuración");
    }
  };

  const handleHistoricalSync = async () => {
    if (!confirm("¿Deseas importar todas las ventas históricas desde Odoo? Esto puede tomar varios minutos.")) return;
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/odoo/sync-historical?from_date=2024-01-01`, null, getAuthHeaders());
      if (response.data.success) {
        toast.success(`Importación histórica completada: ${response.data.synced} ventas`);
        fetchOdooStatus();
      } else {
        toast.error(response.data.error || "Error en importación");
      }
    } catch (error) {
      toast.error("Error en importación histórica");
    } finally {
      setSyncing(false);
    }
  };

  const handleRecalculateMargins = async () => {
    if (!confirm("¿Recalcular márgenes de todas las ventas de Odoo con los porcentajes actuales de los productos?")) return;
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/odoo/recalculate-margins`, null, getAuthHeaders());
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error || "Error al recalcular");
      }
    } catch (error) {
      toast.error("Error al recalcular márgenes");
    } finally {
      setSyncing(false);
    }
  };

  // Duplicate cleanup functions
  const handleCheckDuplicates = async () => {
    setCheckingDuplicates(true);
    try {
      const response = await axios.get(`${API}/admin/check-duplicates`, getAuthHeaders());
      if (response.data.success) {
        setDuplicateStats(response.data);
        if (response.data.has_duplicates) {
          toast.warning(`Se encontraron ${response.data.extra_records} registros duplicados`);
        } else {
          toast.success("No hay duplicados en la base de datos");
        }
      }
    } catch (error) {
      toast.error("Error al verificar duplicados");
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!confirm(`¿Estás seguro de eliminar ${duplicateStats?.extra_records || 0} registros duplicados? Esta acción no se puede deshacer.`)) return;
    
    setCleaningDuplicates(true);
    try {
      const response = await axios.post(`${API}/admin/cleanup-duplicates`, null, getAuthHeaders());
      if (response.data.success) {
        toast.success(response.data.message);
        setDuplicateStats(null);
        fetchOdooStatus();
      } else {
        toast.error(response.data.error || "Error al limpiar duplicados");
      }
    } catch (error) {
      toast.error("Error al limpiar duplicados");
    } finally {
      setCleaningDuplicates(false);
    }
  };

  // Shopify sync functions
  const fetchShopifyStatus = async () => {
    try {
      const response = await axios.get(`${API}/shopify/status`, getAuthHeaders());
      setShopifyStatus(response.data);
    } catch (error) {
      console.log("Shopify status not available");
    }
  };

  const handleShopifySync = async () => {
    setSyncingShopify(true);
    try {
      const response = await axios.post(`${API}/shopify/sync`, null, getAuthHeaders());
      if (response.data.success !== false) {
        toast.success(`Sincronización Shopify completada: ${response.data.synced || 0} pedidos importados`);
        fetchShopifyStatus();
      } else {
        toast.error(response.data.error || "Error en sincronización Shopify");
      }
    } catch (error) {
      toast.error("Error al sincronizar con Shopify");
    } finally {
      setSyncingShopify(false);
    }
  };

  const [fixingShopifySkus, setFixingShopifySkus] = useState(false);
  const [fixingPaymentMethods, setFixingPaymentMethods] = useState(false);
  const [fixingWrongSku, setFixingWrongSku] = useState(false);
  const [skuFixForm, setSkuFixForm] = useState({ old_sku: "", new_sku: "" });
  
  const handleFixShopifySkus = async () => {
    setFixingShopifySkus(true);
    try {
      const response = await axios.post(`${API}/shopify/fix-missing-skus`, null, getAuthHeaders());
      toast.success(response.data.message || "SKUs actualizados correctamente");
    } catch (error) {
      toast.error("Error al actualizar SKUs de Shopify");
    } finally {
      setFixingShopifySkus(false);
    }
  };

  const handleFixPaymentMethods = async () => {
    setFixingPaymentMethods(true);
    try {
      const response = await axios.post(`${API}/odoo/fix-payment-methods`, null, getAuthHeaders());
      toast.success(response.data.message || "Métodos de pago actualizados");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reparar métodos de pago");
    } finally {
      setFixingPaymentMethods(false);
    }
  };

  const handleFixWrongSku = async () => {
    if (!skuFixForm.old_sku.trim() || !skuFixForm.new_sku.trim()) {
      toast.error("Ingresa ambos SKUs");
      return;
    }
    setFixingWrongSku(true);
    try {
      const response = await axios.post(`${API}/sales/fix-sku`, skuFixForm, getAuthHeaders());
      toast.success(response.data.message);
      setSkuFixForm({ old_sku: "", new_sku: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al corregir SKU");
    } finally {
      setFixingWrongSku(false);
    }
  };

  const [relinkingOrphaned, setRelinkingOrphaned] = useState(false);
  const [relinkPreview, setRelinkPreview] = useState(null);

  const [consolidatingOdoo, setConsolidatingOdoo] = useState(false);
  const [consolidatePreview, setConsolidatePreview] = useState(null);

  const [autoRenaming, setAutoRenaming] = useState(false);
  const [autoRenamePreview, setAutoRenamePreview] = useState(null);

  const [deletingOrphans, setDeletingOrphans] = useState(false);
  const [orphansPreview, setOrphansPreview] = useState(null);

  const [batchDiagnose, setBatchDiagnose] = useState(null);
  const [batchResyncing, setBatchResyncing] = useState(false);
  const [batchResyncPreview, setBatchResyncPreview] = useState(null);

  const handleRelinkOrphanedDryRun = async () => {
    setRelinkingOrphaned(true);
    try {
      const response = await axios.post(`${API}/sales/relink-orphaned?dry_run=true`, null, getAuthHeaders());
      setRelinkPreview(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al analizar ventas huérfanas");
    } finally {
      setRelinkingOrphaned(false);
    }
  };

  const handleRelinkOrphanedRun = async () => {
    if (!relinkPreview) {
      toast.error("Primero ejecuta la simulación");
      return;
    }
    if (!window.confirm(`¿Re-vincular permanentemente ${relinkPreview.relinked_total} ventas? Esta acción reescribe los product_id históricos.`)) {
      return;
    }
    setRelinkingOrphaned(true);
    try {
      const response = await axios.post(`${API}/sales/relink-orphaned?dry_run=false`, null, getAuthHeaders());
      setRelinkPreview(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al re-vincular ventas");
    } finally {
      setRelinkingOrphaned(false);
    }
  };

  const handleConsolidateOdooDryRun = async () => {
    setConsolidatingOdoo(true);
    try {
      const response = await axios.post(`${API}/admin/consolidate-odoo-products?dry_run=true`, null, getAuthHeaders());
      setConsolidatePreview(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al analizar productos Odoo");
    } finally {
      setConsolidatingOdoo(false);
    }
  };

  const handleConsolidateOdooRun = async () => {
    if (!consolidatePreview) {
      toast.error("Primero ejecuta la simulación");
      return;
    }
    const msg = `¿Refrescar ${consolidatePreview.canonical_records_refreshed} productos desde Odoo y eliminar ${consolidatePreview.duplicates_removed} duplicados? Las asignaciones a productos internos se preservan.`;
    if (!window.confirm(msg)) return;
    setConsolidatingOdoo(true);
    try {
      const response = await axios.post(`${API}/admin/consolidate-odoo-products?dry_run=false`, null, getAuthHeaders());
      setConsolidatePreview(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al consolidar productos Odoo");
    } finally {
      setConsolidatingOdoo(false);
    }
  };

  const handleAutoRenameDryRun = async () => {
    setAutoRenaming(true);
    try {
      const response = await axios.post(`${API}/admin/auto-rename-internal-products?dry_run=true`, null, getAuthHeaders());
      setAutoRenamePreview(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al analizar productos internos");
    } finally {
      setAutoRenaming(false);
    }
  };

  const handleAutoRenameRun = async () => {
    if (!autoRenamePreview) {
      toast.error("Primero ejecuta la simulación");
      return;
    }
    if (!window.confirm(`¿Renombrar ${autoRenamePreview.renamed_count} productos internos? Solo afecta a los que tienen exactamente 1 producto importado asignado.`)) return;
    setAutoRenaming(true);
    try {
      const response = await axios.post(`${API}/admin/auto-rename-internal-products?dry_run=false`, null, getAuthHeaders());
      setAutoRenamePreview(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al renombrar productos internos");
    } finally {
      setAutoRenaming(false);
    }
  };

  const handleDeleteOrphansDryRun = async () => {
    setDeletingOrphans(true);
    try {
      const response = await axios.post(`${API}/admin/delete-odoo-orphans?dry_run=true&include_assigned=true`, null, getAuthHeaders());
      setOrphansPreview(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al analizar huérfanos");
    } finally {
      setDeletingOrphans(false);
    }
  };

  const handleDeleteOrphansSafe = async () => {
    if (!orphansPreview) return;
    if (!window.confirm(`¿Eliminar ${orphansPreview.orphans_safe_count} productos huérfanos SIN asignación? Es una limpieza segura.`)) return;
    setDeletingOrphans(true);
    try {
      const response = await axios.post(`${API}/admin/delete-odoo-orphans?dry_run=false&include_assigned=false`, null, getAuthHeaders());
      setOrphansPreview(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar huérfanos");
    } finally {
      setDeletingOrphans(false);
    }
  };

  const handleDeleteOrphansAll = async () => {
    if (!orphansPreview) return;
    const total = orphansPreview.orphans_safe_count + orphansPreview.orphans_assigned_count;
    if (!window.confirm(`¿Eliminar TODOS los ${total} productos huérfanos incluyendo los que tienen asignación a Producto Interno? Las asignaciones se desvincularán automáticamente. Esta acción NO se puede deshacer.`)) return;
    setDeletingOrphans(true);
    try {
      const response = await axios.post(`${API}/admin/delete-odoo-orphans?dry_run=false&include_assigned=true`, null, getAuthHeaders());
      setOrphansPreview(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar huérfanos");
    } finally {
      setDeletingOrphans(false);
    }
  };

  const handleBatchDiagnose = async () => {
    setBatchResyncing(true);
    try {
      const response = await axios.get(`${API}/admin/batch-diagnose`, getAuthHeaders());
      setBatchDiagnose(response.data);
      setBatchResyncPreview(null);
      toast.success(`${response.data.needs_resync_count} de ${response.data.total_batches} lotes necesitan resync`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al diagnosticar lotes");
    } finally {
      setBatchResyncing(false);
    }
  };

  const handleBatchResyncDryRun = async () => {
    setBatchResyncing(true);
    try {
      const response = await axios.post(`${API}/admin/batches-resync-skus?dry_run=true&only_active=true`, null, getAuthHeaders());
      setBatchResyncPreview(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al simular resync");
    } finally {
      setBatchResyncing(false);
    }
  };

  const handleBatchResyncRun = async () => {
    if (!batchResyncPreview) {
      toast.error("Primero ejecuta la simulación");
      return;
    }
    if (!window.confirm(`¿Actualizar los SKUs heredados en ${batchResyncPreview.updated} lotes activos? Esto recalcula las piezas vendidas usando los productos importados ACTUALES.`)) return;
    setBatchResyncing(true);
    try {
      const response = await axios.post(`${API}/admin/batches-resync-skus?dry_run=false&only_active=true`, null, getAuthHeaders());
      setBatchResyncPreview(response.data);
      toast.success(response.data.message);
      // Refresh diagnosis after running
      await handleBatchDiagnose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al resincronizar lotes");
    } finally {
      setBatchResyncing(false);
    }
  };

  const [inspecting, setInspecting] = useState({});
  const [inspectResults, setInspectResults] = useState({});
  const [fixingSkus, setFixingSkus] = useState({});
  const [syncingImages, setSyncingImages] = useState(false);
  const [syncImagesResult, setSyncImagesResult] = useState(null);

  const handleSyncProductImages = async () => {
    setSyncingImages(true);
    try {
      const resp = await axios.post(`${API}/admin/sync-product-images?source=all`, null, getAuthHeaders());
      setSyncImagesResult(resp.data);
      const total = resp.data.total || 0;
      toast.success(`Imágenes sincronizadas: ${total} productos actualizados`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al sincronizar imágenes");
    } finally {
      setSyncingImages(false);
    }
  };

  const handleInspectProductSales = async (internalProductId) => {
    setInspecting(prev => ({...prev, [internalProductId]: true}));
    try {
      const response = await axios.get(`${API}/admin/inspect-product-sales?internal_product_id=${internalProductId}`, getAuthHeaders());
      setInspectResults(prev => ({...prev, [internalProductId]: response.data}));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al inspeccionar ventas");
    } finally {
      setInspecting(prev => ({...prev, [internalProductId]: false}));
    }
  };

  const handleAutoFixMissingSkus = async (internalProductId) => {
    if (!window.confirm("¿Auto-rellenar los SKUs faltantes para este producto?\n\nSe asignará el PRIMER SKU esperado a las ventas que NO tienen SKU pero cuyo nombre/notas mencionan al producto. Esta acción modifica la colección de ventas.")) {
      return;
    }
    setFixingSkus(prev => ({...prev, [internalProductId]: true}));
    try {
      // Step 1: dry_run preview
      const previewResp = await axios.post(`${API}/admin/fix-missing-skus-from-notes`,
        { mode: "name_match", internal_product_id: internalProductId, dry_run: true },
        getAuthHeaders()
      );
      const preview = previewResp.data;
      if (!preview.fixed) {
        toast.info(`No hay ventas sin SKU que coincidan con "${preview.internal_product_name}".`);
        return;
      }
      if (!window.confirm(`Se actualizarán ${preview.fixed} ventas (de ${preview.scanned} sin SKU) asignándoles el SKU correspondiente.\n\n¿Aplicar cambios?`)) {
        return;
      }
      // Step 2: actual write
      const writeResp = await axios.post(`${API}/admin/fix-missing-skus-from-notes`,
        { mode: "name_match", internal_product_id: internalProductId, dry_run: false },
        getAuthHeaders()
      );
      const r = writeResp.data;
      toast.success(`Listo: ${r.written} ventas actualizadas para "${r.internal_product_name}".`);
      // Refresh inspection panel
      handleInspectProductSales(internalProductId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al auto-rellenar SKUs");
    } finally {
      setFixingSkus(prev => ({...prev, [internalProductId]: false}));
    }
  };

  const handleToggleShopifyAutoSync = async () => {
    try {
      const newStatus = !shopifyStatus?.sync_enabled;
      const response = await axios.post(`${API}/shopify/toggle-auto-sync?enable=${newStatus}`, null, getAuthHeaders());
      if (response.data.success) {
        toast.success(response.data.message);
        fetchShopifyStatus();
      }
    } catch (error) {
      toast.error("Error al cambiar configuración de Shopify");
    }
  };

  // Reimport all Odoo data
  const handleReimportAll = async () => {
    if (!confirm("⚠️ ATENCIÓN: Esto eliminará TODAS las ventas de Odoo actuales y las reimportará desde cero.\n\nEste proceso puede tardar varios minutos.\n\n¿Estás seguro de continuar?")) return;
    
    setReimporting(true);
    try {
      const response = await axios.post(`${API}/odoo/reimport-all`, null, getAuthHeaders());
      if (response.data.success) {
        toast.success(response.data.message);
        fetchOdooStatus();
        setDuplicateStats(null);
      } else {
        toast.error(response.data.error || "Error al reimportar");
      }
    } catch (error) {
      toast.error("Error al reimportar datos de Odoo");
    } finally {
      setReimporting(false);
    }
  };

  // Update SKUs on existing sales
  const handleUpdateSkus = async () => {
    if (!confirm("Esto actualizará los SKUs de las ventas que no tienen SKU asignado.\n\nEste proceso consulta Odoo para obtener los SKUs correctos.\n\n¿Continuar?")) return;
    
    setUpdatingSkus(true);
    try {
      const response = await axios.post(`${API}/admin/update-sales-skus`, null, getAuthHeaders());
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.error || "Error al actualizar SKUs");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar SKUs");
    } finally {
      setUpdatingSkus(false);
    }
  };

  // Import products from Odoo
  const handleImportOdooProducts = async () => {
    if (!confirm("Esto importará todos los productos del POS de Odoo.\n\nLos productos existentes se actualizarán, los nuevos se crearán.\n\n¿Continuar?")) return;
    
    setImportingOdooProducts(true);
    try {
      const response = await axios.post(`${API}/admin/import-odoo-products`, null, getAuthHeaders());
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData(); // Refresh products list
      } else {
        toast.error(response.data.error || "Error al importar productos");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar productos de Odoo");
    } finally {
      setImportingOdooProducts(false);
    }
  };

  // Cleanup old Odoo categories
  const [cleaningOdooCategories, setCleaningOdooCategories] = useState(false);
  const handleCleanupOdooCategories = async () => {
    if (!confirm("⚠️ ATENCIÓN: Esto eliminará TODOS los productos de Odoo.\n\nDespués de eliminar, debes hacer clic en 'Importar Productos' para reimportar los productos correctos (templates).\n\n¿Continuar?")) return;
    
    setCleaningOdooCategories(true);
    try {
      const response = await axios.delete(`${API}/admin/cleanup-odoo-categories`, getAuthHeaders());
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
      } else {
        toast.error(response.data.error || "Error al limpiar productos");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al limpiar productos de Odoo");
    } finally {
      setCleaningOdooCategories(false);
    }
  };

  // Import products from Shopify
  const handleImportShopifyProducts = async () => {
    if (!confirm("Esto importará todos los productos de Shopify.\n\nLos productos existentes se actualizarán, los nuevos se crearán.\n\n¿Continuar?")) return;
    
    setImportingShopifyProducts(true);
    try {
      const response = await axios.post(`${API}/admin/import-shopify-products`, null, getAuthHeaders());
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData(); // Refresh products list
      } else {
        toast.error(response.data.error || "Error al importar productos");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar productos de Shopify");
    } finally {
      setImportingShopifyProducts(false);
    }
  };

  // Save Shopify credentials
  const handleSaveShopifyCredentials = async (e) => {
    e.preventDefault();
    setSavingShopifyCredentials(true);
    try {
      const response = await axios.post(`${API}/shopify/credentials`, shopifyCredentials, getAuthHeaders());
      if (response.data.success) {
        toast.success("Credenciales de Shopify guardadas correctamente");
        setShowShopifyConfig(false);
        setShopifyCredentials({ store_url: "", client_id: "", client_secret: "" });
        fetchShopifyStatus();
      } else {
        toast.error(response.data.error || "Error al guardar credenciales");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar credenciales de Shopify");
    } finally {
      setSavingShopifyCredentials(false);
    }
  };

  // Odoo credentials functions
  const handleSaveOdooCredentials = async (e) => {
    e.preventDefault();
    setSavingOdooCredentials(true);
    
    try {
      const response = await axios.post(`${API}/odoo/credentials`, odooCredentials, getAuthHeaders());
      if (response.data.success) {
        toast.success(response.data.message);
        setShowOdooConfig(false);
        setOdooCredentials({ url: "", db: "", username: "", api_key: "" });
        fetchOdooStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar credenciales");
    } finally {
      setSavingOdooCredentials(false);
    }
  };

  const handleSaveMappings = async () => {
    const changes = Object.entries(pendingMappings);
    if (changes.length === 0) {
      toast.info("No hay cambios pendientes");
      return;
    }

    setSavingMappings(true);
    try {
      // Save all pending mappings
      await Promise.all(
        changes.map(([productId, parentCategoryId]) =>
          axios.put(`${API}/products/${productId}`, { 
            parent_category_id: parentCategoryId 
          }, getAuthHeaders())
        )
      );
      toast.success(`${changes.length} mapeo(s) guardado(s)`);
      setPendingMappings({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar mapeos");
    } finally {
      setSavingMappings(false);
    }
  };

  const handleDiscardMappings = () => {
    setPendingMappings({});
    toast.info("Cambios descartados");
  };

  // Get effective mapping value (pending or saved)
  const getEffectiveMapping = (product) => {
    if (pendingMappings.hasOwnProperty(product.id)) {
      return pendingMappings[product.id] || "none";
    }
    return product.parent_category_id || "none";
  };

  // Check if a product has pending changes
  const hasPendingChange = (productId) => {
    return pendingMappings.hasOwnProperty(productId);
  };

  // Filter products by source
  const shopifyProducts = products.filter(p => p.source === 'shopify');
  const odooCategories = products.filter(p => p.source === 'odoo');

  const pendingCount = Object.keys(pendingMappings).length;

  const expenseCategories = categories.filter(c => c.type === "expense");
  const incomeCategories = categories.filter(c => c.type === "income");

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Configuración</h1>
          <p className="text-[#6B7280]">Administra categorías, métodos de pago y usuarios</p>
        </div>

        {/* User Info */}
        <Card className="bg-white border-[#FECDD3]">
          <CardHeader>
            <CardTitle className="text-[#4A4A4A] font-['Manrope']">Tu Perfil</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#F43F5E] flex items-center justify-center text-2xl font-bold text-white">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-[#4A4A4A] text-lg font-medium">{user?.name}</p>
                <p className="text-[#6B7280]">{user?.email}</p>
                <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  user?.role === 'admin' 
                    ? 'bg-[#F43F5E]/10 text-[#F43F5E]' 
                    : 'bg-[#3B82F6]/10 text-[#3B82F6]'
                }`}>
                  {user?.role === 'admin' ? (
                    <><ShieldCheck className="w-3 h-3" /> Administrador</>
                  ) : (
                    <><Shield className="w-3 h-3" /> Gerente Heladería</>
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="categories" className="space-y-6">
          <TabsList className="bg-[#FEE2E2] border-[#FECDD3] flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="categories" className="data-[state=active]:bg-[#F43F5E] data-[state=active]:text-white" data-testid="categories-tab">
              <Tag className="w-4 h-4 mr-2" />
              Categorías
            </TabsTrigger>
            <TabsTrigger value="payment-methods" className="data-[state=active]:bg-[#F43F5E] data-[state=active]:text-white" data-testid="payment-methods-tab">
              <CreditCard className="w-4 h-4 mr-2" />
              Métodos de Pago
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="users" className="data-[state=active]:bg-[#F43F5E] data-[state=active]:text-white" data-testid="users-tab">
                <Users className="w-4 h-4 mr-2" />
                Usuarios
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="shopify-mapping" className="data-[state=active]:bg-[#95BF47] data-[state=active]:text-white" data-testid="shopify-mapping-tab">
                <Link2 className="w-4 h-4 mr-2" />
                Mapeo Shopify
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="odoo-sync" className="data-[state=active]:bg-[#8B5CF6] data-[state=active]:text-white" data-testid="odoo-sync-tab">
                <Cloud className="w-4 h-4 mr-2" />
                Odoo
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="shopify-sync" className="data-[state=active]:bg-[#95BF47] data-[state=active]:text-white" data-testid="shopify-sync-tab">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Shopify
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="notifications" className="data-[state=active]:bg-[#3B82F6] data-[state=active]:text-white" data-testid="notifications-tab">
                <Bell className="w-4 h-4 mr-2" />
                Notificaciones
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="inventory-locations" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-white" data-testid="inventory-locations-tab">
                <Boxes className="w-4 h-4 mr-2" />
                Ubicaciones
              </TabsTrigger>
            )}
          </TabsList>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-6">
            {/* Add Category */}
            <div className="flex justify-end">
              <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#F43F5E] hover:bg-[#E11D48] text-white btn-glow" data-testid="add-category-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Categoría
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-[#FECDD3] text-[#4A4A4A]">
                  <DialogHeader>
                    <DialogTitle className="font-['Manrope']">Nueva Categoría</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateCategory} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Nombre</Label>
                      <Input
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                        placeholder="Ej: Servicios"
                        required
                        data-testid="category-name-input"
                        className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#9CA3AF]"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Tipo</Label>
                      <Select value={newCategory.type} onValueChange={(v) => setNewCategory({...newCategory, type: v})}>
                        <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="category-type-select">
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#FECDD3]">
                          <SelectItem value="expense" className="text-[#4A4A4A] hover:bg-[#FEE2E2]">Gasto</SelectItem>
                          <SelectItem value="income" className="text-[#4A4A4A] hover:bg-[#FEE2E2]">Ingreso</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Color</Label>
                      <div className="flex flex-wrap gap-2">
                        {COLORS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setNewCategory({...newCategory, color})}
                            className={`w-8 h-8 rounded-lg transition-transform ${newCategory.color === color ? 'ring-2 ring-[#4A4A4A] scale-110' : ''}`}
                            style={{ backgroundColor: color }}
                            data-testid={`color-${color}`}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)} className="flex-1 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2]">
                        Cancelar
                      </Button>
                      <Button type="submit" className="flex-1 bg-[#F43F5E] hover:bg-[#E11D48] text-white" data-testid="save-category-button">
                        Guardar
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Expense Categories */}
            <Card className="bg-white border-[#FECDD3]">
              <CardHeader>
                <CardTitle className="text-[#4A4A4A] font-['Manrope'] flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#EF4444]"></div>
                  Categorías de Gastos
                </CardTitle>
                <CardDescription className="text-[#6B7280]">
                  {expenseCategories.length} categorías
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {expenseCategories.map((cat) => (
                    <div 
                      key={cat.id}
                      className="flex items-center justify-between p-3 bg-[#FFFAFA] rounded-lg border border-[#FEE2E2] hover:border-[#FECDD3] transition-colors"
                      data-testid={`category-item-${cat.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cat.color }}></div>
                        <span className="text-[#4A4A4A]">{cat.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCategory(cat.id)}
                        data-testid={`delete-category-${cat.id}`}
                        className="text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Income Categories */}
            <Card className="bg-white border-[#FECDD3]">
              <CardHeader>
                <CardTitle className="text-[#4A4A4A] font-['Manrope'] flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
                  Categorías de Ingresos
                </CardTitle>
                <CardDescription className="text-[#6B7280]">
                  {incomeCategories.length} categorías
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {incomeCategories.map((cat) => (
                    <div 
                      key={cat.id}
                      className="flex items-center justify-between p-3 bg-[#FFFAFA] rounded-lg border border-[#FEE2E2] hover:border-[#FECDD3] transition-colors"
                      data-testid={`category-item-${cat.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cat.color }}></div>
                        <span className="text-[#4A4A4A]">{cat.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCategory(cat.id)}
                        data-testid={`delete-category-${cat.id}`}
                        className="text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Methods Tab */}
          <TabsContent value="payment-methods" className="space-y-6">
            {/* Add Payment Method */}
            <div className="flex justify-end">
              <Dialog open={methodDialogOpen} onOpenChange={setMethodDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#F43F5E] hover:bg-[#E11D48] text-white btn-glow" data-testid="add-method-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Método de Pago
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-[#FECDD3] text-[#4A4A4A]">
                  <DialogHeader>
                    <DialogTitle className="font-['Manrope']">Nuevo Método de Pago</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateMethod} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[#6B7280]">Nombre</Label>
                      <Input
                        value={newMethod.name}
                        onChange={(e) => setNewMethod({...newMethod, name: e.target.value})}
                        placeholder="Ej: PayPal"
                        required
                        data-testid="method-name-input"
                        className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#9CA3AF]"
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setMethodDialogOpen(false)} className="flex-1 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2]">
                        Cancelar
                      </Button>
                      <Button type="submit" className="flex-1 bg-[#F43F5E] hover:bg-[#E11D48] text-white" data-testid="save-method-button">
                        Guardar
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Payment Methods List */}
            <Card className="bg-white border-[#FECDD3]">
              <CardHeader>
                <CardTitle className="text-[#4A4A4A] font-['Manrope']">Métodos de Pago</CardTitle>
                <CardDescription className="text-[#6B7280]">
                  {paymentMethods.length} métodos configurados
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {paymentMethods.map((method) => (
                    <div 
                      key={method.id}
                      className="flex items-center justify-between p-3 bg-[#FFFAFA] rounded-lg border border-[#FEE2E2] hover:border-[#FECDD3] transition-colors"
                      data-testid={`method-item-${method.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-4 h-4 text-[#F43F5E]" />
                        <span className="text-[#4A4A4A]">{method.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMethod(method.id)}
                        data-testid={`delete-method-${method.id}`}
                        className="text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab - Admin Only */}
          {isAdmin && (
            <TabsContent value="users" className="space-y-6">
              {/* Add User */}
              <div className="flex justify-end">
                <Dialog open={userDialogOpen} onOpenChange={(open) => { setUserDialogOpen(open); if (!open) resetUserForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#F43F5E] hover:bg-[#E11D48] text-white btn-glow" data-testid="add-user-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo Usuario
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-[#FECDD3] text-[#4A4A4A]">
                    <DialogHeader>
                      <DialogTitle className="font-['Manrope']">
                        {editingUser ? "Editar Usuario" : "Nuevo Usuario"}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveUser} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-[#6B7280]">Nombre</Label>
                        <Input
                          value={userForm.name}
                          onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                          placeholder="Nombre completo"
                          required
                          data-testid="user-name-input"
                          className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#9CA3AF]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[#6B7280]">Email</Label>
                        <Input
                          type="email"
                          value={userForm.email}
                          onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                          placeholder="correo@ejemplo.com"
                          required
                          data-testid="user-email-input"
                          className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#9CA3AF]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[#6B7280]">
                          Contraseña {editingUser && "(dejar vacío para no cambiar)"}
                        </Label>
                        <Input
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                          placeholder="••••••••"
                          required={!editingUser}
                          data-testid="user-password-input"
                          className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] placeholder:text-[#9CA3AF]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-[#6B7280]">Rol</Label>
                        <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v, assigned_categories: v === 'proveedor' ? userForm.assigned_categories : []})}>
                          <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="user-role-select">
                            <SelectValue placeholder="Seleccionar rol" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-[#FECDD3]">
                            <SelectItem value="admin" className="text-[#4A4A4A] hover:bg-[#FEE2E2]">
                              <div className="flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-[#F43F5E]" />
                                Administrador
                              </div>
                            </SelectItem>
                            <SelectItem value="manager" className="text-[#4A4A4A] hover:bg-[#FEE2E2]">
                              <div className="flex items-center gap-2">
                                <Shield className="w-4 h-4 text-[#3B82F6]" />
                                Gerente Heladería
                              </div>
                            </SelectItem>
                            <SelectItem value="proveedor" className="text-[#4A4A4A] hover:bg-[#FEE2E2]">
                              <div className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-[#10B981]" />
                                Proveedor
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Product Assignment for Suppliers */}
                      {userForm.role === 'proveedor' && (
                        <div className="space-y-2">
                          <Label className="text-[#6B7280]">Productos Internos Asignados</Label>
                          <p className="text-xs text-[#9CA3AF]">Selecciona los productos internos que este proveedor suministra (de la sección Merch)</p>
                          <div className="max-h-48 overflow-y-auto border border-[#FECDD3] rounded-lg p-3 space-y-2 bg-[#FFFAFA]">
                            {internalProducts.filter(p => p.is_proveedor).map((product) => (
                              <div key={product.id} className="flex items-center gap-3 p-2 hover:bg-[#FEE2E2] rounded">
                                <Checkbox
                                  id={`internal-product-${product.id}`}
                                  checked={(userForm.assigned_categories || []).includes(product.id)}
                                  onCheckedChange={() => handleToggleProductAssignment(product.id)}
                                  className="border-[#FECDD3] data-[state=checked]:bg-[#F43F5E] data-[state=checked]:border-[#F43F5E]"
                                />
                                <label 
                                  htmlFor={`internal-product-${product.id}`}
                                  className="flex-1 cursor-pointer flex items-center gap-2"
                                >
                                  <Package className="w-4 h-4 text-[#6B7280]" />
                                  <span className="text-[#4A4A4A]">{product.name}</span>
                                  <span className="text-xs bg-[#EDE9FE] text-[#8B5CF6] px-2 py-0.5 rounded">Proveedor</span>
                                </label>
                              </div>
                            ))}
                            {internalProducts.filter(p => p.is_proveedor).length === 0 && (
                              <p className="text-sm text-[#9CA3AF] text-center py-2">
                                No hay productos de proveedor. Ve a Merch → Productos y marca un producto como "Proveedor".
                              </p>
                            )}
                          </div>
                          {(userForm.assigned_categories || []).length > 0 && (
                            <p className="text-xs text-[#10B981]">
                              {userForm.assigned_categories.length} producto(s) seleccionado(s)
                            </p>
                          )}
                        </div>
                      )}

                      <div className="bg-[#FEF3C7] rounded-lg p-3 text-sm text-[#B45309]">
                        <p><strong>Permisos por rol:</strong></p>
                        <ul className="mt-1 ml-4 list-disc">
                          <li><strong>Administrador:</strong> Acceso total, gestión de usuarios</li>
                          <li><strong>Gerente Heladería:</strong> Registrar transacciones y ventas</li>
                          <li><strong>Proveedor:</strong> Ver ventas de sus productos asignados</li>
                        </ul>
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)} className="flex-1 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2]">
                          Cancelar
                        </Button>
                        <Button type="submit" className="flex-1 bg-[#F43F5E] hover:bg-[#E11D48] text-white" data-testid="save-user-button">
                          {editingUser ? "Actualizar" : "Crear Usuario"}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Users List */}
              <Card className="bg-white border-[#FECDD3]">
                <CardHeader>
                  <CardTitle className="text-[#4A4A4A] font-['Manrope']">Usuarios del Sistema</CardTitle>
                  <CardDescription className="text-[#6B7280]">
                    {users.length} usuarios registrados
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Mobile Cards */}
                  <div className="lg:hidden space-y-3">
                    {users.map((u) => (
                      <div 
                        key={u.id}
                        className={`p-4 rounded-lg border ${u.is_active ? 'bg-[#FFFAFA] border-[#FEE2E2]' : 'bg-[#F3F4F6] border-[#E5E7EB]'}`}
                        data-testid={`user-card-${u.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                              u.role === 'admin' ? 'bg-[#F43F5E]' : u.role === 'proveedor' ? 'bg-[#10B981]' : 'bg-[#3B82F6]'
                            }`}>
                              {u.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className={`font-medium ${u.is_active ? 'text-[#4A4A4A]' : 'text-[#9CA3AF]'}`}>{u.name}</p>
                              <p className="text-sm text-[#6B7280]">{u.email}</p>
                            </div>
                          </div>
                          {u.id !== user?.id && (
                            <Switch
                              checked={u.is_active}
                              onCheckedChange={() => handleToggleUserActive(u.id, u.is_active)}
                            />
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            u.role === 'admin' 
                              ? 'bg-[#F43F5E]/10 text-[#F43F5E]' 
                              : u.role === 'proveedor'
                              ? 'bg-[#10B981]/10 text-[#10B981]'
                              : 'bg-[#3B82F6]/10 text-[#3B82F6]'
                          }`}>
                            {u.role === 'admin' ? (
                              <><ShieldCheck className="w-3 h-3" /> Admin</>
                            ) : u.role === 'proveedor' ? (
                              <><Truck className="w-3 h-3" /> Proveedor</>
                            ) : (
                              <><Shield className="w-3 h-3" /> Gerente Heladería</>
                            )}
                          </span>
                          
                          {u.id !== user?.id && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenUserDialog(u)}
                                className="h-8 w-8 p-0 text-[#6B7280] hover:text-[#4A4A4A] hover:bg-[#FEE2E2]"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(u.id)}
                                className="h-8 w-8 p-0 text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>

                        {(u.role === 'manager' || u.role === 'gerente_heladeria') && u.id !== user?.id && (
                          <div className="mt-3 pt-3 border-t border-[#FEE2E2] flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-[#4A4A4A]">Puede editar inventario</p>
                              <p className="text-[10px] text-[#6B7280]">Permite ajustar piezas en Odoo/Shopify.</p>
                            </div>
                            <Switch
                              checked={!!u.can_edit_inventory}
                              onCheckedChange={() => handleToggleCanEditInventory(u.id, !!u.can_edit_inventory)}
                              data-testid={`toggle-can-edit-inventory-mobile-${u.id}`}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Desktop Table */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr className="bg-[#FEE2E2]/50">
                          <th>Usuario</th>
                          <th>Email</th>
                          <th>Rol</th>
                          <th>Productos</th>
                          <th>Editar Inventario</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} data-testid={`user-row-${u.id}`} className={!u.is_active ? 'opacity-60' : ''}>
                            <td>
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                  u.role === 'admin' ? 'bg-[#F43F5E]' : u.role === 'proveedor' ? 'bg-[#10B981]' : 'bg-[#3B82F6]'
                                }`}>
                                  {u.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-[#4A4A4A] font-medium">{u.name}</span>
                                {u.id === user?.id && (
                                  <span className="text-xs bg-[#FEE2E2] text-[#6B7280] px-2 py-0.5 rounded">Tú</span>
                                )}
                              </div>
                            </td>
                            <td className="text-[#6B7280]">{u.email}</td>
                            <td>
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                u.role === 'admin' 
                                  ? 'bg-[#F43F5E]/10 text-[#F43F5E]' 
                                  : u.role === 'proveedor'
                                  ? 'bg-[#10B981]/10 text-[#10B981]'
                                  : 'bg-[#3B82F6]/10 text-[#3B82F6]'
                              }`}>
                                {u.role === 'admin' ? (
                                  <><ShieldCheck className="w-3 h-3" /> Administrador</>
                                ) : u.role === 'proveedor' ? (
                                  <><Truck className="w-3 h-3" /> Proveedor</>
                                ) : (
                                  <><Shield className="w-3 h-3" /> Gerente Heladería</>
                                )}
                              </span>
                            </td>
                            <td>
                              {u.role === 'proveedor' && (u.assigned_categories || []).length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {(u.assigned_categories || []).slice(0, 2).map(prodId => {
                                    const prod = internalProducts.find(p => p.id === prodId);
                                    return prod ? (
                                      <span key={prodId} className="text-xs bg-[#EDE9FE] text-[#8B5CF6] px-2 py-0.5 rounded">
                                        {prod.name}
                                      </span>
                                    ) : (
                                      <span key={prodId} className="text-xs bg-[#FEE2E2] text-[#F43F5E] px-2 py-0.5 rounded">
                                        ID inválido
                                      </span>
                                    );
                                  })}
                                  {(u.assigned_categories || []).length > 2 && (
                                    <span className="text-xs text-[#6B7280]">+{u.assigned_categories.length - 2}</span>
                                  )}
                                </div>
                              ) : u.role === 'proveedor' ? (
                                <span className="text-xs text-[#9CA3AF]">Sin asignar</span>
                              ) : (
                                <span className="text-xs text-[#9CA3AF]">-</span>
                              )}
                            </td>
                            <td>
                              {(u.role === 'manager' || u.role === 'gerente_heladeria') ? (
                                u.id !== user?.id ? (
                                  <Switch
                                    checked={!!u.can_edit_inventory}
                                    onCheckedChange={() => handleToggleCanEditInventory(u.id, !!u.can_edit_inventory)}
                                    data-testid={`toggle-can-edit-inventory-${u.id}`}
                                  />
                                ) : (
                                  <span className="text-xs text-[#9CA3AF]">—</span>
                                )
                              ) : u.role === 'admin' ? (
                                <span className="text-xs text-[#10B981] bg-[#D1FAE5] px-2 py-1 rounded">Siempre</span>
                              ) : u.role === 'gerente_merch' ? (
                                <span className="text-xs text-[#10B981] bg-[#D1FAE5] px-2 py-1 rounded">Siempre</span>
                              ) : (
                                <span className="text-xs text-[#9CA3AF]">N/A</span>
                              )}
                            </td>
                            <td>
                              {u.id !== user?.id ? (
                                <Switch
                                  checked={u.is_active}
                                  onCheckedChange={() => handleToggleUserActive(u.id, u.is_active)}
                                />
                              ) : (
                                <span className="text-xs text-[#10B981] bg-[#D1FAE5] px-2 py-1 rounded">Activo</span>
                              )}
                            </td>
                            <td>
                              {u.id !== user?.id && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleOpenUserDialog(u)}
                                    data-testid={`edit-user-${u.id}`}
                                    className="text-[#6B7280] hover:text-[#4A4A4A] hover:bg-[#FEE2E2]"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteUser(u.id)}
                                    data-testid={`delete-user-${u.id}`}
                                    className="text-[#6B7280] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Shopify Mapping Tab (Admin only) */}
          {isAdmin && (
            <TabsContent value="shopify-mapping" className="space-y-6">
              <Card className="bg-white border-[#95BF47]/30">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#95BF47]/20 flex items-center justify-center">
                        <ShoppingBag className="w-5 h-5 text-[#95BF47]" />
                      </div>
                      <div>
                        <CardTitle className="text-[#4A4A4A] font-['Manrope']">Mapeo de Productos Shopify</CardTitle>
                        <CardDescription className="text-[#6B7280]">
                          Asigna cada producto de Shopify a una categoría de Odoo para unificar márgenes y reportes
                        </CardDescription>
                      </div>
                    </div>
                    {/* Save/Discard buttons */}
                    {pendingCount > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-[#F59E0B] font-medium">
                          {pendingCount} cambio(s) pendiente(s)
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDiscardMappings}
                          className="border-gray-300 text-gray-600 hover:bg-gray-50"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Descartar
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveMappings}
                          disabled={savingMappings}
                          className="bg-[#95BF47] hover:bg-[#7ea339] text-white"
                          data-testid="save-mappings-button"
                        >
                          <Save className="w-4 h-4 mr-1" />
                          {savingMappings ? "Guardando..." : "Guardar Cambios"}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {shopifyProducts.length === 0 ? (
                    <div className="text-center py-8 text-[#6B7280]">
                      <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No hay productos de Shopify sincronizados</p>
                      <p className="text-sm mt-1">Sincroniza Shopify desde la página de Ventas</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Stats */}
                      <div className="flex gap-4 p-4 bg-[#95BF47]/5 rounded-lg border border-[#95BF47]/20">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[#95BF47]">{shopifyProducts.length}</p>
                          <p className="text-xs text-[#6B7280]">Productos Shopify</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[#10B981]">{shopifyProducts.filter(p => getEffectiveMapping(p) !== "none").length}</p>
                          <p className="text-xs text-[#6B7280]">Mapeados</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-[#F59E0B]">{shopifyProducts.filter(p => getEffectiveMapping(p) === "none").length}</p>
                          <p className="text-xs text-[#6B7280]">Sin mapear</p>
                        </div>
                        {pendingCount > 0 && (
                          <div className="text-center border-l border-[#95BF47]/20 pl-4">
                            <p className="text-2xl font-bold text-[#3B82F6]">{pendingCount}</p>
                            <p className="text-xs text-[#6B7280]">Pendientes</p>
                          </div>
                        )}
                      </div>

                      {/* Mapping Table */}
                      <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-[#F9FAFB]">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-medium text-[#6B7280]">Producto Shopify</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-[#6B7280]">Categoría Odoo</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-[#6B7280]">Estado</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#E5E7EB]">
                            {[...shopifyProducts]
                              .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }))
                              .map((product) => {
                              const effectiveMapping = getEffectiveMapping(product);
                              const isPending = hasPendingChange(product.id);
                              const mappedCategory = odooCategories.find(c => c.id === effectiveMapping);
                              
                              return (
                                <tr key={product.id} className={`hover:bg-[#F9FAFB] ${isPending ? 'bg-blue-50/50' : ''}`}>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <ShoppingBag className="w-4 h-4 text-[#95BF47]" />
                                      <span className="font-medium text-[#4A4A4A]">{product.name}</span>
                                      {isPending && (
                                        <span className="text-xs text-[#3B82F6] font-medium">• modificado</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <Select 
                                      value={effectiveMapping} 
                                      onValueChange={(value) => handleLocalMappingChange(product.id, value)}
                                    >
                                      <SelectTrigger 
                                        className={`w-full bg-white ${isPending ? 'border-[#3B82F6] ring-1 ring-[#3B82F6]/20' : 'border-[#E5E7EB]'}`} 
                                        data-testid={`mapping-select-${product.id}`}
                                      >
                                        <SelectValue placeholder="Seleccionar categoría..." />
                                      </SelectTrigger>
                                      <SelectContent className="bg-white">
                                        <SelectItem value="none" className="text-[#9CA3AF]">
                                          Sin asignar
                                        </SelectItem>
                                        {[...odooCategories]
                                          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }))
                                          .map((cat) => (
                                          <SelectItem key={cat.id} value={cat.id}>
                                            <div className="flex items-center gap-2">
                                              <Package className="w-3 h-3 text-[#8B5CF6]" />
                                              {cat.name}
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="px-4 py-3">
                                    {isPending ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[#3B82F6]/10 text-[#3B82F6]">
                                        Pendiente de guardar
                                      </span>
                                    ) : effectiveMapping !== "none" ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[#10B981]/10 text-[#10B981]">
                                        <Link2 className="w-3 h-3" />
                                        Mapeado a {mappedCategory?.name || product.parent_category_name}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[#F59E0B]/10 text-[#F59E0B]">
                                        Sin mapear
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Info */}
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-sm text-blue-700">
                          <strong>¿Qué hace el mapeo?</strong> Al asignar un producto de Shopify a una categoría de Odoo, 
                          las ventas de ese producto se agruparán en los reportes junto con las ventas físicas de esa categoría.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Odoo Sync Tab */}
          {isAdmin && (
            <TabsContent value="odoo-sync" className="space-y-6">
              <Card className="bg-white border-[#8B5CF6]/30">
                <CardHeader>
                  <CardTitle className="text-[#4A4A4A] font-['Manrope'] flex items-center gap-2">
                    <Cloud className="w-5 h-5 text-[#8B5CF6]" />
                    Sincronización con Odoo POS
                  </CardTitle>
                  <CardDescription className="text-[#6B7280]">
                    Configura la sincronización automática de ventas desde Odoo POS
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {odooStatus?.configured ? (
                    <>
                      {/* Status Card */}
                      <div className="p-4 rounded-lg bg-gradient-to-r from-[#8B5CF6]/10 to-[#6366F1]/10 border border-[#8B5CF6]/20">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${odooStatus?.sync_enabled ? 'bg-[#8B5CF6]/20' : 'bg-gray-200'}`}>
                              {odooStatus?.sync_enabled ? (
                                <Cloud className="w-6 h-6 text-[#8B5CF6]" />
                              ) : (
                                <CloudOff className="w-6 h-6 text-gray-500" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-[#4A4A4A]">Estado de Sincronización</h3>
                                <Badge variant={odooStatus?.sync_enabled ? "default" : "secondary"} className={odooStatus?.sync_enabled ? "bg-[#8B5CF6]" : ""}>
                                  {odooStatus?.sync_enabled ? "Activa" : "Pausada"}
                                </Badge>
                              </div>
                              <p className="text-sm text-[#6B7280]">
                                {odooStatus?.odoo_sales_count || 0} ventas importadas • Sincronización cada {odooStatus?.sync_interval_minutes || 5} min
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Sync Now */}
                        <Card className="border-[#FECDD3]">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center">
                                <RefreshCw className="w-5 h-5 text-[#8B5CF6]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Sincronizar Ahora</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Importar ventas recientes de Odoo manualmente
                                </p>
                                <Button
                                  onClick={handleOdooSync}
                                  disabled={syncing}
                                  className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
                                  data-testid="odoo-sync-now-button"
                                >
                                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                                  {syncing ? "Sincronizando..." : "Sincronizar"}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Toggle Auto Sync */}
                        <Card className="border-[#FECDD3]">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${odooStatus?.sync_enabled ? 'bg-[#10B981]/10' : 'bg-gray-100'}`}>
                                {odooStatus?.sync_enabled ? (
                                  <Cloud className="w-5 h-5 text-[#10B981]" />
                                ) : (
                                  <CloudOff className="w-5 h-5 text-gray-500" />
                                )}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Sincronización Automática</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  {odooStatus?.sync_enabled ? "La sincronización está activa" : "La sincronización está pausada"}
                                </p>
                                <Button
                                  onClick={handleToggleAutoSync}
                                  variant={odooStatus?.sync_enabled ? "outline" : "default"}
                                  className={`w-full ${odooStatus?.sync_enabled ? 'border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10' : 'bg-[#10B981] hover:bg-[#059669] text-white'}`}
                                  data-testid="odoo-toggle-auto-sync-button"
                                >
                                  {odooStatus?.sync_enabled ? (
                                    <>
                                      <CloudOff className="w-4 h-4 mr-2" />
                                      Pausar Sincronización
                                    </>
                                  ) : (
                                    <>
                                      <Cloud className="w-4 h-4 mr-2" />
                                      Activar Sincronización
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Historical Sync */}
                        <Card className="border-[#FECDD3]">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                                <RefreshCw className="w-5 h-5 text-[#F59E0B]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Importar Histórico</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Importar todas las ventas desde 2024
                                </p>
                                <Button
                                  onClick={handleHistoricalSync}
                                  disabled={syncing}
                                  variant="outline"
                                  className="w-full border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10"
                                  data-testid="odoo-historical-sync-button"
                                >
                                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                                  Importar Histórico
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Recalculate Margins */}
                        <Card className="border-[#FECDD3]">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center">
                                <RefreshCw className="w-5 h-5 text-[#3B82F6]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Recalcular Márgenes</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Actualizar márgenes con porcentajes actuales
                                </p>
                                <Button
                                  onClick={handleRecalculateMargins}
                                  disabled={syncing}
                                  variant="outline"
                                  className="w-full border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10"
                                  data-testid="odoo-recalculate-margins-button"
                                >
                                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                                  Recalcular Márgenes
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Maintenance Section - Duplicate Cleanup */}
                      <Card className="border-[#EF4444]/20 bg-white">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base text-[#4A4A4A] flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-[#EF4444]" />
                            Mantenimiento de Datos
                          </CardTitle>
                          <CardDescription>
                            Verificar y limpiar registros duplicados
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Status */}
                          {duplicateStats && (
                            <div className={`p-4 rounded-lg ${duplicateStats.has_duplicates ? 'bg-[#FEF3C7] border border-[#F59E0B]/30' : 'bg-[#D1FAE5] border border-[#10B981]/30'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                {duplicateStats.has_duplicates ? (
                                  <AlertTriangle className="w-5 h-5 text-[#F59E0B]" />
                                ) : (
                                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                                )}
                                <span className={`font-medium ${duplicateStats.has_duplicates ? 'text-[#B45309]' : 'text-[#047857]'}`}>
                                  {duplicateStats.has_duplicates ? 'Duplicados encontrados' : 'Sin duplicados'}
                                </span>
                              </div>
                              {duplicateStats.has_duplicates && (
                                <div className="text-sm text-[#6B7280] space-y-1">
                                  <p>• Referencias duplicadas: <strong>{duplicateStats.duplicate_references}</strong></p>
                                  <p>• Registros extra: <strong>{duplicateStats.extra_records}</strong></p>
                                  <p>• Monto inflado: <strong>${duplicateStats.extra_amount?.toLocaleString()}</strong></p>
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="flex gap-3">
                            <Button
                              onClick={handleCheckDuplicates}
                              disabled={checkingDuplicates || cleaningDuplicates || reimporting}
                              variant="outline"
                              className="flex-1 border-[#6B7280] text-[#6B7280] hover:bg-[#6B7280]/10"
                              data-testid="check-duplicates-button"
                            >
                              <RefreshCw className={`w-4 h-4 mr-2 ${checkingDuplicates ? 'animate-spin' : ''}`} />
                              Verificar Duplicados
                            </Button>
                            
                            {duplicateStats?.has_duplicates && (
                              <Button
                                onClick={handleCleanupDuplicates}
                                disabled={cleaningDuplicates || checkingDuplicates || reimporting}
                                className="flex-1 bg-[#EF4444] hover:bg-[#DC2626] text-white"
                                data-testid="cleanup-duplicates-button"
                              >
                                <Trash2 className={`w-4 h-4 mr-2 ${cleaningDuplicates ? 'animate-spin' : ''}`} />
                                Limpiar {duplicateStats.extra_records} Duplicados
                              </Button>
                            )}
                          </div>

                          {/* Reimport All Button */}
                          <div className="pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#DC2626]/10 flex items-center justify-center">
                                <RefreshCw className="w-5 h-5 text-[#DC2626]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Reimportar Todo</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Elimina todas las ventas de Odoo y las reimporta desde cero. Usa esto si los datos están corruptos o incompletos.
                                </p>
                                <Button
                                  onClick={handleReimportAll}
                                  disabled={reimporting || syncing}
                                  className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                                  data-testid="reimport-all-button"
                                >
                                  <RefreshCw className={`w-4 h-4 mr-2 ${reimporting ? 'animate-spin' : ''}`} />
                                  {reimporting ? 'Reimportando...' : 'Reimportar Todas las Ventas de Odoo'}
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Update SKUs Button */}
                          <div className="pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 flex items-center justify-center">
                                <Tag className="w-5 h-5 text-[#3B82F6]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Actualizar SKUs de Ventas</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Actualiza los SKUs de las ventas existentes consultando Odoo. Necesario para que los lotes de Merch contabilicen ventas históricas correctamente.
                                </p>
                                <Button
                                  onClick={handleUpdateSkus}
                                  disabled={updatingSkus || syncing}
                                  className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                                  data-testid="update-skus-button"
                                >
                                  <Tag className={`w-4 h-4 mr-2 ${updatingSkus ? 'animate-spin' : ''}`} />
                                  {updatingSkus ? 'Actualizando SKUs...' : 'Actualizar SKUs de Ventas'}
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Fix Payment Methods Button */}
                          <div className="pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#2563EB]/10 flex items-center justify-center">
                                <CreditCard className="w-5 h-5 text-[#2563EB]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Reparar Métodos de Pago</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Consulta Odoo para clasificar ventas existentes como Efectivo o Tarjeta. Necesario para ver el desglose en el Dashboard.
                                </p>
                                <Button
                                  onClick={handleFixPaymentMethods}
                                  disabled={fixingPaymentMethods || syncing}
                                  className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                                  data-testid="fix-payment-methods-button"
                                >
                                  <CreditCard className={`w-4 h-4 mr-2 ${fixingPaymentMethods ? 'animate-spin' : ''}`} />
                                  {fixingPaymentMethods ? 'Reparando...' : 'Reparar Métodos de Pago'}
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Relink Orphaned Sales */}
                          <div className="pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center">
                                <Link2 className="w-5 h-5 text-[#7C3AED]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Re-vincular Ventas Huérfanas</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Repara permanentemente ventas históricas cuyo producto fue re-importado/borrado. Usa SKU y nombre del campo notas para recalcular <code>product_id</code> y eliminar la dependencia de parseo en tiempo real.
                                </p>
                                {relinkPreview && (
                                  <div className="mb-3 p-3 rounded-md bg-[#F3F4F6] text-xs space-y-1 text-[#4A4A4A] border border-[#E5E7EB]">
                                    <div className="font-semibold">Simulación:</div>
                                    <div>Total escaneadas: <span className="font-medium">{relinkPreview.total_sales_scanned}</span></div>
                                    <div>Huérfanas encontradas: <span className="font-medium">{relinkPreview.orphan_sales_found}</span></div>
                                    <div>Se re-vincularán: <span className="font-semibold text-[#10B981]">{relinkPreview.relinked_total}</span> ({relinkPreview.relinked_by_sku} SKU + {relinkPreview.relinked_by_name} nombre)</div>
                                    <div>No recuperables: <span className="font-medium text-[#EF4444]">{relinkPreview.still_orphan}</span></div>
                                  </div>
                                )}
                                <div className="flex gap-2">
                                  <Button
                                    onClick={handleRelinkOrphanedDryRun}
                                    disabled={relinkingOrphaned || syncing}
                                    variant="outline"
                                    className="flex-1 border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/10"
                                    data-testid="relink-orphaned-dryrun-button"
                                  >
                                    <Eye className={`w-4 h-4 mr-2 ${relinkingOrphaned ? 'animate-spin' : ''}`} />
                                    {relinkingOrphaned ? 'Analizando...' : 'Simular'}
                                  </Button>
                                  <Button
                                    onClick={handleRelinkOrphanedRun}
                                    disabled={relinkingOrphaned || syncing || !relinkPreview || relinkPreview.relinked_total === 0}
                                    className="flex-1 bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                                    data-testid="relink-orphaned-run-button"
                                  >
                                    <Link2 className={`w-4 h-4 mr-2 ${relinkingOrphaned ? 'animate-spin' : ''}`} />
                                    {relinkingOrphaned ? 'Re-vinculando...' : 'Ejecutar Re-vinculación'}
                                  </Button>
                                </div>
                                {relinkPreview && !relinkPreview.dry_run && (
                                  <p className="text-xs text-[#10B981] mt-2 font-medium">
                                    ✓ {relinkPreview.relinked_total} ventas re-vinculadas exitosamente.
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Consolidate Odoo Products (force name/SKU refresh + de-dup) */}
                          <div className="pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#0EA5E9]/10 flex items-center justify-center">
                                <Package className="w-5 h-5 text-[#0EA5E9]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Refrescar y Consolidar Productos Odoo</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Fuerza la actualización de <strong>nombre, SKU y variantes</strong> desde Odoo y elimina registros duplicados (mismos SKUs en distintos productos). Las asignaciones a productos internos se mantienen.
                                </p>
                                {consolidatePreview && (
                                  <div className="mb-3 p-3 rounded-md bg-[#F3F4F6] text-xs space-y-1 text-[#4A4A4A] border border-[#E5E7EB]">
                                    <div className="font-semibold">Simulación:</div>
                                    <div>Plantillas en Odoo: <span className="font-medium">{consolidatePreview.odoo_templates_seen}</span></div>
                                    <div>Productos locales Odoo: <span className="font-medium">{consolidatePreview.local_odoo_products_total}</span> ({consolidatePreview.local_odoo_products_matched} matcheados, <span className="text-[#EF4444] font-semibold">{consolidatePreview.local_odoo_products_orphan} huérfanos</span>)</div>
                                    <div>Productos a refrescar: <span className="font-semibold text-[#0EA5E9]">{consolidatePreview.canonical_records_refreshed}</span></div>
                                    <div>Cambios de nombre: <span className="font-semibold text-[#F59E0B]">{consolidatePreview.name_changes_count}</span></div>
                                    <div>Duplicados a eliminar: <span className="font-semibold text-[#EF4444]">{consolidatePreview.duplicates_removed}</span></div>
                                    {consolidatePreview.name_changes_sample?.length > 0 && (
                                      <details className="mt-2">
                                        <summary className="cursor-pointer text-[#6B7280] hover:text-[#4A4A4A]">Ver cambios de nombre (muestra)</summary>
                                        <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                                          {consolidatePreview.name_changes_sample.map((c, i) => (
                                            <li key={i} className="text-[10px] truncate">
                                              <span className="text-[#EF4444] line-through">{c.old_name || '(vacío)'}</span>
                                              {' → '}
                                              <span className="text-[#10B981] font-medium">{c.new_name}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </details>
                                    )}
                                    {consolidatePreview.orphan_locals_sample?.length > 0 && (
                                      <details className="mt-2" open>
                                        <summary className="cursor-pointer text-[#EF4444] font-semibold hover:text-[#B91C1C]">⚠ Productos locales SIN match en Odoo ({consolidatePreview.local_odoo_products_orphan})</summary>
                                        <p className="text-[10px] text-[#6B7280] mt-1 mb-1 italic">Estos productos están marcados como Odoo en la app pero no se encontraron en Odoo actualmente (template borrado o SKU cambiado).</p>
                                        <ul className="mt-1 space-y-0.5 max-h-48 overflow-y-auto">
                                          {consolidatePreview.orphan_locals_sample.map((c, i) => (
                                            <li key={i} className="text-[10px] truncate border-b border-gray-200 pb-0.5">
                                              <span className="font-medium text-[#4A4A4A]">{c.name}</span>
                                              {c.sku && <span className="text-[#6B7280]"> · SKU: {c.sku}</span>}
                                              {c.variant_skus?.length > 0 && <span className="text-[#6B7280]"> · Variantes: {c.variant_skus.join(', ')}</span>}
                                              {c.odoo_template_id && <span className="text-[#9CA3AF]"> · tpl: {c.odoo_template_id}</span>}
                                            </li>
                                          ))}
                                        </ul>
                                      </details>
                                    )}
                                  </div>
                                )}
                                <div className="flex gap-2">
                                  <Button
                                    onClick={handleConsolidateOdooDryRun}
                                    disabled={consolidatingOdoo || syncing}
                                    variant="outline"
                                    className="flex-1 border-[#0EA5E9] text-[#0EA5E9] hover:bg-[#0EA5E9]/10"
                                    data-testid="consolidate-odoo-dryrun-button"
                                  >
                                    <Eye className={`w-4 h-4 mr-2 ${consolidatingOdoo ? 'animate-spin' : ''}`} />
                                    {consolidatingOdoo ? 'Analizando...' : 'Simular'}
                                  </Button>
                                  <Button
                                    onClick={handleConsolidateOdooRun}
                                    disabled={consolidatingOdoo || syncing || !consolidatePreview || (consolidatePreview.canonical_records_refreshed === 0 && consolidatePreview.duplicates_removed === 0)}
                                    className="flex-1 bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
                                    data-testid="consolidate-odoo-run-button"
                                  >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${consolidatingOdoo ? 'animate-spin' : ''}`} />
                                    {consolidatingOdoo ? 'Ejecutando...' : 'Refrescar y Consolidar'}
                                  </Button>
                                </div>
                                {consolidatePreview && !consolidatePreview.dry_run && (
                                  <p className="text-xs text-[#10B981] mt-2 font-medium">
                                    ✓ {consolidatePreview.canonical_records_refreshed} productos refrescados, {consolidatePreview.duplicates_removed} duplicados eliminados.
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Delete Odoo Orphan Products */}
                          <div className="pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
                                <Trash2 className="w-5 h-5 text-[#EF4444]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Eliminar Productos Odoo Huérfanos</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Elimina productos locales marcados como Odoo que ya <strong>no existen en Odoo</strong> (template borrado, SKU cambiado, o categorías auto-creadas como "Helado", "Peluches"). Indispensable después de renombrar productos en Odoo.
                                </p>
                                {orphansPreview && (
                                  <div className="mb-3 p-3 rounded-md bg-[#F3F4F6] text-xs space-y-1 text-[#4A4A4A] border border-[#E5E7EB]">
                                    <div className="font-semibold">Análisis:</div>
                                    <div>Huérfanos seguros (sin asignación): <span className="font-semibold text-[#10B981]">{orphansPreview.orphans_safe_count}</span></div>
                                    <div>Huérfanos con asignación a Producto Interno: <span className="font-semibold text-[#F59E0B]">{orphansPreview.orphans_assigned_count}</span></div>
                                    {orphansPreview.orphans_safe_sample?.length > 0 && (
                                      <details className="mt-2">
                                        <summary className="cursor-pointer text-[#10B981] font-medium">Ver huérfanos seguros ({orphansPreview.orphans_safe_count})</summary>
                                        <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                                          {orphansPreview.orphans_safe_sample.map((c, i) => (
                                            <li key={i} className="text-[10px] truncate">
                                              <span className="font-medium">{c.name}</span>
                                              {c.sku && <span className="text-[#6B7280]"> · {c.sku}</span>}
                                            </li>
                                          ))}
                                        </ul>
                                      </details>
                                    )}
                                    {orphansPreview.orphans_assigned_sample?.length > 0 && (
                                      <details className="mt-2">
                                        <summary className="cursor-pointer text-[#F59E0B] font-medium">⚠ Ver huérfanos con asignación ({orphansPreview.orphans_assigned_count})</summary>
                                        <p className="text-[10px] text-[#6B7280] mt-1 italic">Al eliminarlos se desvincularán del producto interno. Si los necesitas, reasígnalos primero a una versión nueva del producto Odoo.</p>
                                        <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                                          {orphansPreview.orphans_assigned_sample.map((c, i) => (
                                            <li key={i} className="text-[10px] truncate">
                                              <span className="font-medium">{c.name}</span>
                                              {c.sku && <span className="text-[#6B7280]"> · {c.sku}</span>}
                                            </li>
                                          ))}
                                        </ul>
                                      </details>
                                    )}
                                  </div>
                                )}
                                <div className="flex gap-2 flex-wrap">
                                  <Button
                                    onClick={handleDeleteOrphansDryRun}
                                    disabled={deletingOrphans || syncing}
                                    variant="outline"
                                    className="flex-1 min-w-[120px] border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                                    data-testid="orphans-analyze-button"
                                  >
                                    <Eye className={`w-4 h-4 mr-2 ${deletingOrphans ? 'animate-spin' : ''}`} />
                                    {deletingOrphans ? 'Analizando...' : 'Analizar'}
                                  </Button>
                                  <Button
                                    onClick={handleDeleteOrphansSafe}
                                    disabled={deletingOrphans || syncing || !orphansPreview || orphansPreview.orphans_safe_count === 0}
                                    className="flex-1 min-w-[160px] bg-[#10B981] hover:bg-[#059669] text-white"
                                    data-testid="orphans-delete-safe-button"
                                  >
                                    <Trash2 className={`w-4 h-4 mr-2 ${deletingOrphans ? 'animate-spin' : ''}`} />
                                    Eliminar Seguros ({orphansPreview?.orphans_safe_count || 0})
                                  </Button>
                                  <Button
                                    onClick={handleDeleteOrphansAll}
                                    disabled={deletingOrphans || syncing || !orphansPreview || (orphansPreview.orphans_safe_count + orphansPreview.orphans_assigned_count) === 0}
                                    className="flex-1 min-w-[160px] bg-[#EF4444] hover:bg-[#B91C1C] text-white"
                                    data-testid="orphans-delete-all-button"
                                  >
                                    <Trash2 className={`w-4 h-4 mr-2 ${deletingOrphans ? 'animate-spin' : ''}`} />
                                    Eliminar Todos ({(orphansPreview?.orphans_safe_count || 0) + (orphansPreview?.orphans_assigned_count || 0)})
                                  </Button>
                                </div>
                                {orphansPreview && !orphansPreview.dry_run && (
                                  <p className="text-xs text-[#10B981] mt-2 font-medium">
                                    ✓ {orphansPreview.deleted_count} productos eliminados, {orphansPreview.assignments_unlinked} asignaciones desvinculadas.
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Auto-rename Internal Products (margin_categories) */}
                          <div className="pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#A855F7]/10 flex items-center justify-center">
                                <Tag className="w-5 h-5 text-[#A855F7]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Renombrar Productos Internos (1:1)</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Renombra automáticamente los <strong>productos internos</strong> que tienen <strong>exactamente 1</strong> producto importado asignado, usando el nombre del producto. Los productos internos con 2+ asignaciones (agrupaciones manuales) se respetan.
                                </p>
                                {autoRenamePreview && (
                                  <div className="mb-3 p-3 rounded-md bg-[#F3F4F6] text-xs space-y-1 text-[#4A4A4A] border border-[#E5E7EB]">
                                    <div className="font-semibold">Simulación:</div>
                                    <div>Total productos internos: <span className="font-medium">{autoRenamePreview.total_internal_products}</span></div>
                                    <div>A renombrar: <span className="font-semibold text-[#A855F7]">{autoRenamePreview.renamed_count}</span></div>
                                    <div>Omitidos (2+ asignaciones): <span className="font-medium">{autoRenamePreview.skipped_multi_assignment}</span></div>
                                    <div>Omitidos (sin asignar): <span className="font-medium">{autoRenamePreview.skipped_no_assignment}</span></div>
                                    {autoRenamePreview.renames_sample?.length > 0 && (
                                      <details className="mt-2">
                                        <summary className="cursor-pointer text-[#6B7280] hover:text-[#4A4A4A]">Ver cambios de nombre (muestra)</summary>
                                        <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                                          {autoRenamePreview.renames_sample.map((c, i) => (
                                            <li key={i} className="text-[10px] truncate">
                                              <span className="text-[#EF4444] line-through">{c.old_name || '(vacío)'}</span>
                                              {' → '}
                                              <span className="text-[#10B981] font-medium">{c.new_name}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </details>
                                    )}
                                  </div>
                                )}
                                <div className="flex gap-2">
                                  <Button
                                    onClick={handleAutoRenameDryRun}
                                    disabled={autoRenaming || syncing}
                                    variant="outline"
                                    className="flex-1 border-[#A855F7] text-[#A855F7] hover:bg-[#A855F7]/10"
                                    data-testid="auto-rename-dryrun-button"
                                  >
                                    <Eye className={`w-4 h-4 mr-2 ${autoRenaming ? 'animate-spin' : ''}`} />
                                    {autoRenaming ? 'Analizando...' : 'Simular'}
                                  </Button>
                                  <Button
                                    onClick={handleAutoRenameRun}
                                    disabled={autoRenaming || syncing || !autoRenamePreview || autoRenamePreview.renamed_count === 0}
                                    className="flex-1 bg-[#A855F7] hover:bg-[#9333EA] text-white"
                                    data-testid="auto-rename-run-button"
                                  >
                                    <Tag className={`w-4 h-4 mr-2 ${autoRenaming ? 'animate-spin' : ''}`} />
                                    {autoRenaming ? 'Renombrando...' : 'Renombrar Automáticamente'}
                                  </Button>
                                </div>
                                {autoRenamePreview && !autoRenamePreview.dry_run && (
                                  <p className="text-xs text-[#10B981] mt-2 font-medium">
                                    ✓ {autoRenamePreview.renamed_count} productos internos renombrados.
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Sync Product Images */}
                          <div className="pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#EC4899]/10 flex items-center justify-center">
                                <Package className="w-5 h-5 text-[#EC4899]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Sincronizar Imágenes de Productos</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Rellena los <strong>thumbnails</strong> de productos importados. Jala imágenes desde <strong>Shopify</strong> (URL CDN) y <strong>Odoo</strong> (base64). No borra nada, sólo agrega imágenes faltantes o actualiza las existentes.
                                </p>
                                <Button
                                  onClick={handleSyncProductImages}
                                  disabled={syncingImages || syncing}
                                  variant="outline"
                                  className="border-[#EC4899] text-[#EC4899] hover:bg-[#EC4899]/10"
                                  data-testid="sync-product-images-button"
                                >
                                  <RefreshCw className={`w-4 h-4 mr-2 ${syncingImages ? 'animate-spin' : ''}`} />
                                  {syncingImages ? 'Sincronizando...' : 'Sincronizar Imágenes'}
                                </Button>
                                {syncImagesResult && (
                                  <div className="mt-3 p-2 rounded bg-[#F9FAFB] text-xs border border-[#E5E7EB] space-y-1">
                                    <div className="text-[#10B981]">
                                      ✓ Total: <strong>{syncImagesResult.total}</strong> productos con imagen actualizada
                                    </div>
                                    <div className="text-[#6B7280]">
                                      Shopify: {syncImagesResult.updated?.shopify || 0} · Odoo: {syncImagesResult.updated?.odoo || 0}
                                    </div>
                                    {syncImagesResult.errors && (
                                      <div className="text-[#EF4444]">
                                        {Object.entries(syncImagesResult.errors).map(([k, v]) => (
                                          <div key={k}>⚠ {k}: {v}</div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Batch SKU Diagnose & Resync */}
                          <div className="pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#06B6D4]/10 flex items-center justify-center">
                                <Package className="w-5 h-5 text-[#06B6D4]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Diagnosticar y Resincronizar SKUs de Lotes</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Los <strong>lotes "congelan" los SKUs</strong> al momento de crearlos. Si después agregaste o quitaste productos importados al Producto Interno, el lote sigue contando solo los SKUs viejos. Usa esto para diagnosticar diferencias y resincronizar.
                                </p>
                                <div className="flex gap-2 flex-wrap mb-3">
                                  <Button
                                    onClick={handleBatchDiagnose}
                                    disabled={batchResyncing || syncing}
                                    variant="outline"
                                    className="flex-1 min-w-[150px] border-[#06B6D4] text-[#06B6D4] hover:bg-[#06B6D4]/10"
                                    data-testid="batch-diagnose-button"
                                  >
                                    <Eye className={`w-4 h-4 mr-2 ${batchResyncing ? 'animate-spin' : ''}`} />
                                    {batchResyncing ? 'Cargando...' : 'Diagnosticar Lotes'}
                                  </Button>
                                  <Button
                                    onClick={handleBatchResyncDryRun}
                                    disabled={batchResyncing || syncing}
                                    variant="outline"
                                    className="flex-1 min-w-[150px] border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10"
                                    data-testid="batch-resync-dryrun-button"
                                  >
                                    <Eye className={`w-4 h-4 mr-2 ${batchResyncing ? 'animate-spin' : ''}`} />
                                    Simular Resync
                                  </Button>
                                  <Button
                                    onClick={handleBatchResyncRun}
                                    disabled={batchResyncing || syncing || !batchResyncPreview || batchResyncPreview.updated === 0}
                                    className="flex-1 min-w-[150px] bg-[#06B6D4] hover:bg-[#0891B2] text-white"
                                    data-testid="batch-resync-run-button"
                                  >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${batchResyncing ? 'animate-spin' : ''}`} />
                                    Resincronizar ({batchResyncPreview?.updated || 0})
                                  </Button>
                                </div>

                                {batchResyncPreview && batchResyncPreview.dry_run && batchResyncPreview.changes_sample?.length > 0 && (
                                  <div className="mb-3 p-3 rounded-md bg-[#FEF3C7] border border-[#F59E0B]/30 text-xs space-y-1">
                                    <div className="font-semibold text-[#92400E]">Cambios pendientes ({batchResyncPreview.updated}):</div>
                                    <ul className="max-h-48 overflow-y-auto space-y-1 mt-1">
                                      {batchResyncPreview.changes_sample.map((c, i) => (
                                        <li key={i} className="text-[10px] border-b border-[#F59E0B]/20 pb-1">
                                          <div className="font-semibold">Lote #{c.batch_number}</div>
                                          {c.added.length > 0 && <div className="text-[#10B981]">+ Agregar: {c.added.join(', ')}</div>}
                                          {c.removed.length > 0 && <div className="text-[#EF4444]">- Quitar: {c.removed.join(', ')}</div>}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {batchDiagnose && (
                                  <div className="p-3 rounded-md bg-[#F3F4F6] text-xs border border-[#E5E7EB]">
                                    <div className="font-semibold mb-2">
                                      Diagnóstico: {batchDiagnose.needs_resync_count}/{batchDiagnose.total_batches} lotes necesitan resync
                                    </div>
                                    <div className="max-h-96 overflow-y-auto space-y-2">
                                      {batchDiagnose.batches.map((b) => (
                                        <div key={b.batch_id} className={`p-2 rounded border ${b.needs_resync ? 'border-[#F59E0B] bg-[#FEF3C7]/30' : 'border-[#E5E7EB] bg-white'}`}>
                                          <div className="flex items-center justify-between flex-wrap gap-1">
                                            <div className="font-semibold text-[#4A4A4A]">
                                              {b.internal_product_name || '(sin Producto Interno)'} · Lote #{b.batch_number}
                                            </div>
                                            <div className="text-[10px] text-[#6B7280]">
                                              {b.status} {b.manually_completed ? '(manual)' : ''}
                                            </div>
                                          </div>
                                          <div className="text-[10px] mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
                                            <div>
                                              <span className="text-[#6B7280]">SKUs congelados ({b.frozen_skus.length}):</span>{' '}
                                              <span className="font-mono">{b.frozen_skus.join(', ') || '∅'}</span>
                                            </div>
                                            <div>
                                              <span className="text-[#6B7280]">SKUs actuales ({b.current_skus.length}):</span>{' '}
                                              <span className="font-mono">{b.current_skus.join(', ') || '∅'}</span>
                                            </div>
                                          </div>
                                          {b.missing_in_frozen.length > 0 && (
                                            <div className="text-[10px] mt-1 text-[#EF4444]">
                                              ⚠ Faltan en lote: <span className="font-mono">{b.missing_in_frozen.join(', ')}</span>
                                            </div>
                                          )}
                                          {b.missing_breakdown.length > 0 && (
                                            <div className="text-[10px] mt-1 text-[#F59E0B]">
                                              💰 Piezas perdidas: {b.missing_breakdown.map(m => `${m.sku} (${m.extra_pieces} pzs ${m.sources.join('/')})`).join(', ')}
                                            </div>
                                          )}
                                          <div className="text-[10px] mt-1 text-[#6B7280]">
                                            Piezas con SKUs congelados: <span className="font-semibold">{b.frozen_pieces_matched}</span>
                                            {' → '}
                                            Piezas con SKUs actuales: <span className="font-semibold text-[#0EA5E9]">{b.current_pieces_matched}</span>
                                            {b.diff_pieces !== 0 && <span className={b.diff_pieces > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}> ({b.diff_pieces > 0 ? '+' : ''}{b.diff_pieces})</span>}
                                          </div>
                                          {b.internal_product_id && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                              <Button
                                                onClick={() => handleInspectProductSales(b.internal_product_id)}
                                                disabled={inspecting[b.internal_product_id]}
                                                size="sm"
                                                variant="outline"
                                                className="h-6 text-[10px] border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10"
                                                data-testid={`inspect-sales-${b.batch_id}`}
                                              >
                                                <Eye className={`w-3 h-3 mr-1 ${inspecting[b.internal_product_id] ? 'animate-spin' : ''}`} />
                                                {inspecting[b.internal_product_id] ? 'Buscando...' : 'Inspeccionar ventas reales'}
                                              </Button>
                                              {inspectResults[b.internal_product_id] && inspectResults[b.internal_product_id].no_sku_but_text_match.rows > 0 && (
                                                <Button
                                                  onClick={() => handleAutoFixMissingSkus(b.internal_product_id)}
                                                  disabled={fixingSkus[b.internal_product_id]}
                                                  size="sm"
                                                  variant="outline"
                                                  className="h-6 text-[10px] border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
                                                  data-testid={`autofix-skus-${b.batch_id}`}
                                                >
                                                  {fixingSkus[b.internal_product_id] ? 'Aplicando...' : `Auto-rellenar SKUs (${inspectResults[b.internal_product_id].no_sku_but_text_match.rows})`}
                                                </Button>
                                              )}
                                              {inspectResults[b.internal_product_id] && (
                                                <div className="mt-2 p-2 rounded bg-white border border-[#E5E7EB] text-[10px] space-y-1">
                                                  <div className="text-[#10B981]">
                                                    ✓ Con SKU correcto: <strong>{inspectResults[b.internal_product_id].matched.pieces} pzs</strong> ({inspectResults[b.internal_product_id].matched.rows} ventas) {Object.entries(inspectResults[b.internal_product_id].matched.by_source).map(([s, v]) => `${s}: ${v.pieces}`).join(', ')}
                                                  </div>
                                                  <div className="text-[#EF4444]">
                                                    ✗ Sin SKU pero nombre matchea: <strong>{inspectResults[b.internal_product_id].no_sku_but_text_match.pieces} pzs</strong> ({inspectResults[b.internal_product_id].no_sku_but_text_match.rows} ventas) {Object.entries(inspectResults[b.internal_product_id].no_sku_but_text_match.by_source).map(([s, v]) => `${s}: ${v.pieces}`).join(', ')}
                                                  </div>
                                                  <div className="text-[#F59E0B]">
                                                    ⚠ SKU distinto pero nombre matchea: <strong>{inspectResults[b.internal_product_id].other_sku_text_match.pieces} pzs</strong> ({inspectResults[b.internal_product_id].other_sku_text_match.rows} ventas) {Object.entries(inspectResults[b.internal_product_id].other_sku_text_match.by_source).map(([s, v]) => `${s}: ${v.pieces}`).join(', ')}
                                                  </div>
                                                  {inspectResults[b.internal_product_id].no_sku_but_text_match.sample.length > 0 && (
                                                    <details className="mt-1">
                                                      <summary className="cursor-pointer text-[#EF4444] hover:text-[#B91C1C]">Ver muestra sin SKU ({inspectResults[b.internal_product_id].no_sku_but_text_match.sample.length})</summary>
                                                      <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto pl-2">
                                                        {inspectResults[b.internal_product_id].no_sku_but_text_match.sample.map((r, i) => (
                                                          <li key={i} className="truncate">{r.date} · {r.source} · qty={r.quantity} · {r.notes}</li>
                                                        ))}
                                                      </ul>
                                                    </details>
                                                  )}
                                                  {inspectResults[b.internal_product_id].other_sku_text_match.sample.length > 0 && (
                                                    <details className="mt-1">
                                                      <summary className="cursor-pointer text-[#F59E0B] hover:text-[#92400E]">Ver muestra con SKU distinto ({inspectResults[b.internal_product_id].other_sku_text_match.sample.length})</summary>
                                                      <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto pl-2">
                                                        {inspectResults[b.internal_product_id].other_sku_text_match.sample.map((r, i) => (
                                                          <li key={i} className="truncate">{r.date} · {r.source} · sku={r.product_sku} · qty={r.quantity} · {r.notes}</li>
                                                        ))}
                                                      </ul>
                                                    </details>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Import Odoo Products Button */}
                          <div className="pt-4 border-t border-[#E5E7EB]">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                                <Package className="w-5 h-5 text-[#10B981]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Importar Productos de Odoo</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Importa productos del POS de Odoo. Los existentes se actualizan preservando asignaciones, y los nuevos se crean automáticamente.
                                </p>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={handleImportOdooProducts}
                                    disabled={importingOdooProducts || syncing}
                                    className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white"
                                    data-testid="import-odoo-products-button"
                                  >
                                    <Package className={`w-4 h-4 mr-2 ${importingOdooProducts ? 'animate-spin' : ''}`} />
                                    {importingOdooProducts ? 'Importando...' : 'Importar Productos'}
                                  </Button>
                                  <Button
                                    onClick={async () => {
                                      try {
                                        const res = await axios.post(`${API}/admin/cleanup-odoo-categories`, null, getAuthHeaders());
                                        toast.success(res.data.message);
                                      } catch (e) {
                                        toast.error(e.response?.data?.detail || 'Error al limpiar categorías');
                                      }
                                    }}
                                    variant="outline"
                                    className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
                                  >
                                    Limpiar Categorías
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <p className="text-xs text-[#9CA3AF]">
                            La importación es no-destructiva: preserva las asignaciones a productos internos.
                          </p>
                        </CardContent>
                      </Card>

                      {/* Info */}
                      <div className="p-4 bg-[#8B5CF6]/5 rounded-lg border border-[#8B5CF6]/20">
                        <p className="text-sm text-[#6B7280]">
                          <strong className="text-[#8B5CF6]">Nota:</strong> Usa "Recalcular Márgenes" después de cambiar los porcentajes de los productos para actualizar los cálculos de todas las ventas importadas.
                        </p>
                      </div>

                      {/* Config Button */}
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          onClick={() => setShowOdooConfig(!showOdooConfig)}
                          className="border-[#8B5CF6]/30 text-[#8B5CF6]"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          {showOdooConfig ? "Ocultar Configuración" : "Cambiar Credenciales"}
                        </Button>
                      </div>

                      {/* Config Form (collapsible) */}
                      {showOdooConfig && (
                        <Card className="border-[#8B5CF6]/20 bg-[#8B5CF6]/5">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base text-[#4A4A4A] flex items-center gap-2">
                              <Key className="w-4 h-4 text-[#8B5CF6]" />
                              Configurar Credenciales de Odoo
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <form onSubmit={handleSaveOdooCredentials} className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>URL de Odoo</Label>
                                  <Input
                                    placeholder="https://tuempresa.odoo.com"
                                    value={odooCredentials.url}
                                    onChange={(e) => setOdooCredentials({...odooCredentials, url: e.target.value})}
                                    required
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Base de Datos</Label>
                                  <Input
                                    placeholder="nombre_db"
                                    value={odooCredentials.db}
                                    onChange={(e) => setOdooCredentials({...odooCredentials, db: e.target.value})}
                                    required
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Usuario (Email)</Label>
                                  <Input
                                    type="email"
                                    placeholder="usuario@empresa.com"
                                    value={odooCredentials.username}
                                    onChange={(e) => setOdooCredentials({...odooCredentials, username: e.target.value})}
                                    required
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>API Key</Label>
                                  <Input
                                    type="password"
                                    placeholder="Tu API Key de Odoo"
                                    value={odooCredentials.api_key}
                                    onChange={(e) => setOdooCredentials({...odooCredentials, api_key: e.target.value})}
                                    required
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    setShowOdooConfig(false);
                                    setOdooCredentials({ url: "", db: "", username: "", api_key: "" });
                                  }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  type="submit"
                                  disabled={savingOdooCredentials}
                                  className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
                                >
                                  {savingOdooCredentials ? "Probando conexión..." : "Guardar y Probar"}
                                </Button>
                              </div>
                            </form>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  ) : (
                    <div className="space-y-6">
                      <div className="p-8 text-center">
                        <CloudOff className="w-16 h-16 mx-auto text-[#9CA3AF]/30 mb-4" />
                        <h3 className="text-lg font-medium text-[#4A4A4A]">Odoo no configurado</h3>
                        <p className="text-[#6B7280] mt-1 mb-4">
                          Configura las credenciales para conectar con Odoo POS
                        </p>
                        <Button
                          onClick={() => setShowOdooConfig(true)}
                          className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Configurar Conexión
                        </Button>
                      </div>

                      {/* Config Form */}
                      {showOdooConfig && (
                        <Card className="border-[#8B5CF6]/20 bg-[#8B5CF6]/5">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base text-[#4A4A4A] flex items-center gap-2">
                              <Key className="w-4 h-4 text-[#8B5CF6]" />
                              Configurar Credenciales de Odoo
                            </CardTitle>
                            <CardDescription>
                              Ingresa las credenciales de tu cuenta de Odoo
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <form onSubmit={handleSaveOdooCredentials} className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>URL de Odoo</Label>
                                  <Input
                                    placeholder="https://tuempresa.odoo.com"
                                    value={odooCredentials.url}
                                    onChange={(e) => setOdooCredentials({...odooCredentials, url: e.target.value})}
                                    required
                                  />
                                  <p className="text-xs text-[#6B7280]">Ej: https://darkarco.odoo.com</p>
                                </div>
                                <div className="space-y-2">
                                  <Label>Base de Datos</Label>
                                  <Input
                                    placeholder="nombre_db"
                                    value={odooCredentials.db}
                                    onChange={(e) => setOdooCredentials({...odooCredentials, db: e.target.value})}
                                    required
                                  />
                                  <p className="text-xs text-[#6B7280]">Ej: darkarco</p>
                                </div>
                                <div className="space-y-2">
                                  <Label>Usuario (Email)</Label>
                                  <Input
                                    type="email"
                                    placeholder="usuario@empresa.com"
                                    value={odooCredentials.username}
                                    onChange={(e) => setOdooCredentials({...odooCredentials, username: e.target.value})}
                                    required
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>API Key</Label>
                                  <Input
                                    type="password"
                                    placeholder="Tu API Key de Odoo"
                                    value={odooCredentials.api_key}
                                    onChange={(e) => setOdooCredentials({...odooCredentials, api_key: e.target.value})}
                                    required
                                  />
                                  <p className="text-xs text-[#6B7280]">Genera el API Key en Odoo: Configuración → Usuarios → Tu Usuario → Preferencias → Claves API</p>
                                </div>
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    setShowOdooConfig(false);
                                    setOdooCredentials({ url: "", db: "", username: "", api_key: "" });
                                  }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  type="submit"
                                  disabled={savingOdooCredentials}
                                  className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
                                >
                                  {savingOdooCredentials ? "Probando conexión..." : "Guardar y Probar"}
                                </Button>
                              </div>
                            </form>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Shopify Sync Tab */}
          {isAdmin && (
            <TabsContent value="shopify-sync" className="space-y-6">
              <Card className="bg-white border-[#95BF47]/30">
                <CardHeader>
                  <CardTitle className="text-[#4A4A4A] font-['Manrope'] flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-[#95BF47]" />
                    Sincronización con Shopify
                  </CardTitle>
                  <CardDescription className="text-[#6B7280]">
                    Configura la sincronización automática de pedidos desde tu tienda Shopify
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {shopifyStatus?.configured ? (
                    <>
                      {/* Status Card */}
                      <div className="p-4 rounded-lg bg-gradient-to-r from-[#95BF47]/10 to-[#5E8E3E]/10 border border-[#95BF47]/20">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${shopifyStatus?.sync_enabled ? 'bg-[#95BF47]/20' : 'bg-gray-200'}`}>
                              {shopifyStatus?.sync_enabled ? (
                                <ShoppingBag className="w-6 h-6 text-[#95BF47]" />
                              ) : (
                                <ShoppingBag className="w-6 h-6 text-gray-500" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-[#4A4A4A]">Estado de Sincronización</h3>
                                <Badge variant={shopifyStatus?.sync_enabled ? "default" : "secondary"} className={shopifyStatus?.sync_enabled ? "bg-[#95BF47]" : ""}>
                                  {shopifyStatus?.sync_enabled ? "Activa" : "Pausada"}
                                </Badge>
                                {shopifyStatus?.connected && (
                                  <Badge variant="outline" className="text-xs bg-green-50 text-green-600 border-green-200">
                                    Conectado
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-[#6B7280]">
                                {shopifyStatus?.orders_synced || shopifyStatus?.sales_created || 0} pedidos importados • Sincronización cada {shopifyStatus?.sync_interval_minutes || 10} min
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Sync Now */}
                        <Card className="border-[#FECDD3]">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#95BF47]/10 flex items-center justify-center">
                                <RefreshCw className="w-5 h-5 text-[#95BF47]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Sincronizar Ahora</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Importar pedidos recientes de Shopify manualmente
                                </p>
                                <Button
                                  onClick={handleShopifySync}
                                  disabled={syncingShopify}
                                  className="w-full bg-[#95BF47] hover:bg-[#5E8E3E] text-white"
                                  data-testid="shopify-sync-now-button"
                                >
                                  <RefreshCw className={`w-4 h-4 mr-2 ${syncingShopify ? 'animate-spin' : ''}`} />
                                  {syncingShopify ? "Sincronizando..." : "Sincronizar"}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Toggle Auto Sync */}
                        <Card className="border-[#FECDD3]">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${shopifyStatus?.sync_enabled ? 'bg-[#10B981]/10' : 'bg-gray-100'}`}>
                                {shopifyStatus?.sync_enabled ? (
                                  <Cloud className="w-5 h-5 text-[#10B981]" />
                                ) : (
                                  <CloudOff className="w-5 h-5 text-gray-500" />
                                )}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Sincronización Automática</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  {shopifyStatus?.sync_enabled ? "La sincronización está activa" : "La sincronización está pausada"}
                                </p>
                                <Button
                                  onClick={handleToggleShopifyAutoSync}
                                  variant={shopifyStatus?.sync_enabled ? "outline" : "default"}
                                  className={`w-full ${shopifyStatus?.sync_enabled ? 'border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10' : 'bg-[#10B981] hover:bg-[#059669] text-white'}`}
                                  data-testid="shopify-toggle-auto-sync-button"
                                >
                                  {shopifyStatus?.sync_enabled ? (
                                    <>
                                      <CloudOff className="w-4 h-4 mr-2" />
                                      Pausar Sincronización
                                    </>
                                  ) : (
                                    <>
                                      <Cloud className="w-4 h-4 mr-2" />
                                      Activar Sincronización
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Fix Missing SKUs */}
                        <Card className="border-[#FECDD3]">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                                <Wrench className="w-5 h-5 text-[#F59E0B]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Reparar SKUs Faltantes</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Actualiza las ventas de Shopify que no tienen SKU guardado. Esto es necesario para que los lotes calculen correctamente las piezas vendidas online.
                                </p>
                                <Button
                                  onClick={handleFixShopifySkus}
                                  disabled={fixingShopifySkus}
                                  className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white"
                                  data-testid="fix-shopify-skus-button"
                                >
                                  <Wrench className={`w-4 h-4 mr-2 ${fixingShopifySkus ? 'animate-spin' : ''}`} />
                                  {fixingShopifySkus ? "Reparando..." : "Reparar SKUs"}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Fix Wrong SKU */}
                        <Card className="border-[#FECDD3]">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
                                <Wrench className="w-5 h-5 text-[#EF4444]" />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-medium text-[#4A4A4A]">Corregir SKU Incorrecto</h4>
                                <p className="text-sm text-[#6B7280] mb-3">
                                  Reemplaza un SKU incorrecto en todas las ventas existentes. Útil cuando se cambió el SKU de un producto en Shopify u Odoo.
                                </p>
                                <div className="space-y-2 mb-3">
                                  <Input
                                    placeholder="SKU incorrecto (ej: PLCH-MEQUIBARA-2)"
                                    value={skuFixForm.old_sku}
                                    onChange={(e) => setSkuFixForm({ ...skuFixForm, old_sku: e.target.value })}
                                    className="bg-white border-[#FECDD3]"
                                    data-testid="fix-sku-old-input"
                                  />
                                  <Input
                                    placeholder="SKU correcto (ej: PLCH-MEQUIBARA)"
                                    value={skuFixForm.new_sku}
                                    onChange={(e) => setSkuFixForm({ ...skuFixForm, new_sku: e.target.value })}
                                    className="bg-white border-[#FECDD3]"
                                    data-testid="fix-sku-new-input"
                                  />
                                </div>
                                <Button
                                  onClick={handleFixWrongSku}
                                  disabled={fixingWrongSku || !skuFixForm.old_sku || !skuFixForm.new_sku}
                                  className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white"
                                  data-testid="fix-wrong-sku-button"
                                >
                                  <Wrench className={`w-4 h-4 mr-2 ${fixingWrongSku ? 'animate-spin' : ''}`} />
                                  {fixingWrongSku ? "Corrigiendo..." : "Corregir SKU"}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Import Products */}
                      <div className="pt-4 border-t border-[#E5E7EB]">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#95BF47]/10 flex items-center justify-center">
                            <Package className="w-5 h-5 text-[#95BF47]" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-[#4A4A4A]">Importar Productos de Shopify</h4>
                            <p className="text-sm text-[#6B7280] mb-3">
                              Importa todos los productos de tu tienda Shopify. Los productos existentes se actualizarán con la información más reciente.
                            </p>
                            <div className="flex gap-2">
                              <Button
                                onClick={handleImportShopifyProducts}
                                disabled={importingShopifyProducts || syncingShopify}
                                className="flex-1 bg-[#95BF47] hover:bg-[#5E8E3E] text-white"
                                data-testid="import-shopify-products-button"
                              >
                                <Package className={`w-4 h-4 mr-2 ${importingShopifyProducts ? 'animate-spin' : ''}`} />
                                {importingShopifyProducts ? 'Importando...' : 'Importar Productos'}
                              </Button>
                              <Button
                                onClick={async () => {
                                  try {
                                    const res = await axios.post(`${API}/admin/cleanup-shopify-duplicates`, null, getAuthHeaders());
                                    toast.success(res.data.message);
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || 'Error al limpiar duplicados');
                                  }
                                }}
                                variant="outline"
                                className="border-[#95BF47] text-[#95BF47] hover:bg-[#95BF47]/10"
                              >
                                Limpiar Duplicados
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="p-4 bg-[#95BF47]/5 rounded-lg border border-[#95BF47]/20">
                        <p className="text-sm text-[#6B7280]">
                          <strong className="text-[#95BF47]">Tip:</strong> Usa la pestaña "Mapeo Shopify" para asignar productos de Shopify a categorías de Odoo y unificar los reportes de ventas.
                        </p>
                      </div>

                      {/* Change Credentials Button */}
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          onClick={() => setShowShopifyConfig(true)}
                          className="border-[#95BF47] text-[#95BF47] hover:bg-[#95BF47]/10"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Cambiar Credenciales
                        </Button>
                      </div>

                      {/* Config Form (when editing) */}
                      {showShopifyConfig && (
                        <Card className="border-[#95BF47]/20 bg-[#95BF47]/5">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base text-[#4A4A4A] flex items-center gap-2">
                              <Key className="w-4 h-4 text-[#95BF47]" />
                              Actualizar Credenciales de Shopify
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <form onSubmit={handleSaveShopifyCredentials} className="space-y-4">
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <Label>URL de la Tienda</Label>
                                  <Input
                                    placeholder="tu-tienda.myshopify.com"
                                    value={shopifyCredentials.store_url}
                                    onChange={(e) => setShopifyCredentials({...shopifyCredentials, store_url: e.target.value})}
                                    required
                                  />
                                  <p className="text-xs text-[#6B7280]">Ej: darkar-co.myshopify.com</p>
                                </div>
                                <div className="space-y-2">
                                  <Label>API Key (Client ID)</Label>
                                  <Input
                                    placeholder="API Key de Shopify"
                                    value={shopifyCredentials.client_id}
                                    onChange={(e) => setShopifyCredentials({...shopifyCredentials, client_id: e.target.value})}
                                    required
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>API Secret (Client Secret)</Label>
                                  <Input
                                    type="password"
                                    placeholder="API Secret de Shopify"
                                    value={shopifyCredentials.client_secret}
                                    onChange={(e) => setShopifyCredentials({...shopifyCredentials, client_secret: e.target.value})}
                                    required
                                  />
                                  <p className="text-xs text-[#6B7280]">
                                    Obtén estas credenciales en tu Admin de Shopify → Configuración → Apps → Desarrollar apps
                                  </p>
                                </div>
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    setShowShopifyConfig(false);
                                    setShopifyCredentials({ store_url: "", client_id: "", client_secret: "" });
                                  }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  type="submit"
                                  disabled={savingShopifyCredentials}
                                  className="bg-[#95BF47] hover:bg-[#5E8E3E] text-white"
                                >
                                  {savingShopifyCredentials ? "Guardando..." : "Guardar Credenciales"}
                                </Button>
                              </div>
                            </form>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  ) : (
                    <div className="space-y-6">
                      <div className="p-8 text-center">
                        <ShoppingBag className="w-16 h-16 mx-auto text-[#9CA3AF]/30 mb-4" />
                        <h3 className="text-lg font-medium text-[#4A4A4A]">Shopify no configurado</h3>
                        <p className="text-[#6B7280] mt-1 mb-4">
                          Configura las credenciales de tu tienda Shopify para sincronizar productos y pedidos
                        </p>
                        <Button
                          onClick={() => setShowShopifyConfig(true)}
                          className="bg-[#95BF47] hover:bg-[#5E8E3E] text-white"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Configurar Shopify
                        </Button>
                      </div>

                      {/* Config Form */}
                      {showShopifyConfig && (
                        <Card className="border-[#95BF47]/20 bg-[#95BF47]/5">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base text-[#4A4A4A] flex items-center gap-2">
                              <Key className="w-4 h-4 text-[#95BF47]" />
                              Configurar Credenciales de Shopify
                            </CardTitle>
                            <CardDescription>
                              Ingresa las credenciales de API de tu tienda Shopify
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <form onSubmit={handleSaveShopifyCredentials} className="space-y-4">
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <Label>URL de la Tienda</Label>
                                  <Input
                                    placeholder="tu-tienda.myshopify.com"
                                    value={shopifyCredentials.store_url}
                                    onChange={(e) => setShopifyCredentials({...shopifyCredentials, store_url: e.target.value})}
                                    required
                                    className="bg-white"
                                  />
                                  <p className="text-xs text-[#6B7280]">Ej: darkar-co.myshopify.com (sin https://)</p>
                                </div>
                                <div className="space-y-2">
                                  <Label>API Key (Client ID)</Label>
                                  <Input
                                    placeholder="API Key de tu app de Shopify"
                                    value={shopifyCredentials.client_id}
                                    onChange={(e) => setShopifyCredentials({...shopifyCredentials, client_id: e.target.value})}
                                    required
                                    className="bg-white"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>API Secret (Client Secret)</Label>
                                  <Input
                                    type="password"
                                    placeholder="API Secret de tu app de Shopify"
                                    value={shopifyCredentials.client_secret}
                                    onChange={(e) => setShopifyCredentials({...shopifyCredentials, client_secret: e.target.value})}
                                    required
                                    className="bg-white"
                                  />
                                  <p className="text-xs text-[#6B7280]">
                                    Para obtener las credenciales: Admin de Shopify → Configuración → Apps y canales de venta → Desarrollar apps → Crear app → Configurar API
                                  </p>
                                </div>
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() => {
                                    setShowShopifyConfig(false);
                                    setShopifyCredentials({ store_url: "", client_id: "", client_secret: "" });
                                  }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  type="submit"
                                  disabled={savingShopifyCredentials}
                                  className="bg-[#95BF47] hover:bg-[#5E8E3E] text-white"
                                >
                                  {savingShopifyCredentials ? "Probando conexión..." : "Guardar y Probar"}
                                </Button>
                              </div>
                            </form>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Notifications Tab */}
          {isAdmin && (
            <TabsContent value="notifications" className="space-y-6" data-testid="notifications-content">
              <Card className="bg-white border-[#BFDBFE]">
                <CardHeader className="bg-[#3B82F6]/10 border-b border-[#BFDBFE]">
                  <CardTitle className="text-[#1D4ED8] font-['Manrope'] flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Notificaciones por Correo
                  </CardTitle>
                  <CardDescription className="text-[#6B7280]">
                    Configura las notificaciones automáticas del sistema
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* Email Configuration */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-[#4A4A4A] flex items-center gap-2">
                        <Mail className="w-4 h-4 text-[#3B82F6]" />
                        Correo de Notificaciones
                      </Label>
                      <Input 
                        type="text"
                        value={notificationSettings.notification_email}
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, notification_email: e.target.value })}
                        placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
                        className="bg-white border-[#BFDBFE] focus:border-[#3B82F6]"
                        data-testid="notification-email-input"
                      />
                      <p className="text-xs text-[#6B7280]">
                        Puedes agregar varios correos separados por coma
                      </p>
                    </div>

                    {/* Notification Types */}
                    <div className="p-4 bg-[#F0F9FF] rounded-lg border border-[#BFDBFE]">
                      <h4 className="font-medium text-[#1D4ED8] mb-3">Tipos de Notificaciones</h4>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#10B981]/20 flex items-center justify-center">
                            <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                          </div>
                          <div>
                            <p className="font-medium text-[#4A4A4A]">Recuperación de Inversión</p>
                            <p className="text-xs text-[#6B7280]">Recibe un correo cuando un lote recupere el 100% de su inversión</p>
                          </div>
                        </div>
                        <Switch 
                          checked={notificationSettings.batch_recovery_notifications}
                          onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, batch_recovery_notifications: checked })}
                          data-testid="batch-recovery-switch"
                        />
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-[#E5E7EB]">
                      <Button 
                        onClick={saveNotificationSettings} 
                        disabled={savingNotifications}
                        className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                        data-testid="save-notifications-btn"
                      >
                        {savingNotifications ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Guardar Configuración
                      </Button>
                      <Button 
                        onClick={sendTestEmail}
                        disabled={sendingTestEmail || !notificationSettings.notification_email}
                        variant="outline"
                        className="border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10"
                        data-testid="test-email-btn"
                      >
                        {sendingTestEmail ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4 mr-2" />
                        )}
                        Enviar Correo de Prueba
                      </Button>
                    </div>

                    {/* Info Box */}
                    <div className="p-4 bg-[#FEF3C7] rounded-lg border border-[#F59E0B]/30">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-[#92400E]">
                          <p className="font-medium mb-1">Nota importante</p>
                          <p>
                            El correo de recuperación se envía <strong>una sola vez</strong> cuando el lote alcanza el 100% de recuperación. 
                            Si el lote ya está recuperado, no recibirás notificación al configurar el correo.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="inventory-locations" className="space-y-6" data-testid="inventory-locations-content" onFocusCapture={fetchInventoryLocations}>
              <Card className="bg-white border-[#F59E0B]/30">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="text-[#F59E0B] flex items-center gap-2">
                        <Boxes className="w-5 h-5" /> Ubicaciones de inventario
                      </CardTitle>
                      <CardDescription>
                        Oculta las ubicaciones que no quieres ver en la pantalla de <b>Inventario</b>. Útil cuando manejas un warehouse central de Shopify y quieres ocultar sucursales que solo usas como "recoger en tienda".
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchInventoryLocations}
                      disabled={invLocLoading}
                      className="border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10"
                      data-testid="reload-locations-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-1 ${invLocLoading ? "animate-spin" : ""}`} /> Actualizar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                    <strong>Nota:</strong> ocultar una ubicación solo afecta la vista en esta app. Odoo/Shopify siguen manejando el stock normalmente. Puedes volver a mostrarla cuando quieras.
                  </div>

                  {/* Odoo section */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="border-[#8B5CF6] text-[#8B5CF6]">Odoo</Badge>
                      <h4 className="text-sm font-semibold text-[#4A4A4A]">Ubicaciones internas</h4>
                    </div>
                    {invAllLocations.odoo.length === 0 ? (
                      <p className="text-xs text-[#9CA3AF] italic">Sin conexión a Odoo o sin ubicaciones. Toca "Actualizar".</p>
                    ) : (
                      <div className="space-y-1">
                        {invAllLocations.odoo.map((l) => {
                          const hidden = isLocationHidden("odoo", l.id);
                          return (
                            <div key={`odoo-${l.id}`} className="flex items-center gap-3 px-3 py-2 rounded border border-[#F3F4F6] hover:bg-[#FEF7ED]/50">
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm ${hidden ? "text-[#9CA3AF] line-through" : "text-[#4A4A4A]"}`}>{l.name}</div>
                                <div className="text-[10px] text-[#9CA3AF]">ID {l.id}</div>
                              </div>
                              <span className="text-xs text-[#6B7280] hidden sm:inline">{hidden ? "Oculta" : "Visible"}</span>
                              <Switch
                                checked={!hidden}
                                disabled={invLocSaving}
                                onCheckedChange={() => toggleLocationHidden("odoo", l)}
                                data-testid={`toggle-loc-odoo-${l.id}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Shopify section */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="border-[#10B981] text-[#10B981]">Shopify</Badge>
                      <h4 className="text-sm font-semibold text-[#4A4A4A]">Ubicaciones de la tienda</h4>
                    </div>
                    {invAllLocations.shopify.length === 0 ? (
                      <p className="text-xs text-[#9CA3AF] italic">Sin conexión a Shopify o sin ubicaciones. Toca "Actualizar".</p>
                    ) : (
                      <div className="space-y-1">
                        {invAllLocations.shopify.map((l) => {
                          const hidden = isLocationHidden("shopify", l.id);
                          return (
                            <div key={`shopify-${l.id}`} className="flex items-center gap-3 px-3 py-2 rounded border border-[#F3F4F6] hover:bg-[#ECFDF5]/50">
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm ${hidden ? "text-[#9CA3AF] line-through" : "text-[#4A4A4A]"}`}>{l.name}</div>
                                <div className="text-[10px] text-[#9CA3AF]">ID {l.id}</div>
                              </div>
                              <span className="text-xs text-[#6B7280] hidden sm:inline">{hidden ? "Oculta" : "Visible"}</span>
                              <Switch
                                checked={!hidden}
                                disabled={invLocSaving}
                                onCheckedChange={() => toggleLocationHidden("shopify", l)}
                                data-testid={`toggle-loc-shopify-${l.id}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};

export default SettingsPage;
